import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { toastSuccess, toastError, toastInfo } from "../utils/toast";
import { api } from "../utils/api";
import { buildMonthlyAvailabilityReport } from "../utils/monthlyAvailabilityReport";
import { getMonthlyExportDates } from "../utils/turniExport";
import {
  cloneAssignmentDrafts,
  formatAssignmentsForExport,
  getPendingAssignmentsForSlot,
  mergePersistedAndPendingAssignments,
} from "../utils/assignmentDrafts";
import { registerMontserrat, FONT_MONTSERRAT } from "../utils/pdfFonts";
import {
  PlayIcon,
  ClockIcon,
  MapPinIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon } from "@heroicons/react/24/solid";
import TurnVolunteerName, { getDailyExceptionText } from "../components/TurnVolunteerName";
import VolunteerPicker from "../components/VolunteerPicker";

const formatLocalDate = (year, month, day) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

// Lazy loader per jsPDF/autotable, evita di caricare i moduli nel main bundle
let cachedJsPDF = null;
const loadJsPdf = async () => {
  if (cachedJsPDF) return cachedJsPDF;
  const [{ default: jsPDF }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  cachedJsPDF = jsPDF;
  return jsPDF;
};

const toLocalISODate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return formatLocalDate(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate()
    );
  }
  if (typeof value === "string") {
    if (value.includes("T")) {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      return formatLocalDate(
        parsed.getFullYear(),
        parsed.getMonth() + 1,
        parsed.getDate()
      );
    }
    const [year, month, day] = value.split("-").map((part) => part.trim());
    if (!year || !month || !day) return null;
    return formatLocalDate(year, month, day);
  }
  return null;
};

const getWeekStartISO = (dateValue) => {
  const isoDate = toLocalISODate(dateValue);
  if (!isoDate) return null;
  const [year, month, day] = isoDate
    .split("-")
    .map((part) => parseInt(part, 10));
  if (!year || !month || !day) return null;
  const baseDate = new Date(year, month - 1, day);
  const dayOfWeek = baseDate.getDay();
  const offset = (dayOfWeek + 6) % 7; // Monday = 0
  const monday = new Date(year, month - 1, day - offset);
  return formatLocalDate(
    monday.getFullYear(),
    monday.getMonth() + 1,
    monday.getDate()
  );
};

const formatDateForTooltip = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

const Autocompilazione = () => {
  const { user: _user, activeCongregazione } = useAuth();

  // Funzione per calcolare il range mensile
  const getMonthRange = (year, month) => {
    // Calcola il primo giorno del mese
    const firstDay = new Date(year, month, 1);

    // Calcola l'ultimo giorno del mese usando il metodo corretto
    const lastDay = new Date(year, month + 1, 0);

    // Formatta le date in formato YYYY-MM-DD evitando problemi di timezone
    const formatDateToISO = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      inizio: formatDateToISO(firstDay),
      fine: formatDateToISO(lastDay),
    };
  };

  // Funzione per calcolare il mese corrente
  const getCurrentMonth = () => {
    const today = new Date();
    return {
      year: today.getFullYear(),
      month: today.getMonth(),
    };
  };

  // Stati principali
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Calcola il mese corrente per l'inizializzazione
  const currentMonth = getCurrentMonth();
  const currentMonthRange = getMonthRange(
    currentMonth.year,
    currentMonth.month
  );

  // Mese selezionato per la visualizzazione
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedDateRange, setSelectedDateRange] = useState(currentMonthRange);

  // Stati responsività
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Stati per autocompilazione
  const [compiling, setCompiling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [resettingInfo, setResettingInfo] = useState(null);

  // Stati per gestione draft locale
  const [pendingAssignments, setPendingAssignmentsState] = useState(new Map()); // Modifiche in sospeso
  const pendingAssignmentsRef = useRef(new Map());
  const exportDraftSnapshotRef = useRef(new Map());
  // Gli handler di export possono essere richiamati subito dopo una selezione
  // manuale. Il ref viene aggiornato sincronicamente insieme allo stato React,
  // così l'export legge sempre l'ultima bozza già visibile nella griglia.
  const setPendingAssignments = (nextAssignments) => {
    const resolvedAssignments =
      typeof nextAssignments === "function"
        ? nextAssignments(pendingAssignmentsRef.current)
        : nextAssignments;
    pendingAssignmentsRef.current = resolvedAssignments;
    setPendingAssignmentsState(resolvedAssignments);
  };
  const [pendingRemovals, setPendingRemovals] = useState(new Map()); // Rimozioni in sospeso
  const [manuallyEmptiedSlots, setManuallyEmptiedSlots] = useState(new Set()); // Slot lasciati vuoti manualmente dall'utente (formato: "date-slotId-postazioneId")
  const [showInstructions, setShowInstructions] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [isZeroAvailabilityReportCollapsed, setIsZeroAvailabilityReportCollapsed] =
    useState(true);
  const [isAvailableUnassignedReportCollapsed, setIsAvailableUnassignedReportCollapsed] =
    useState(true);
  const [isUnavailableForMonthReportCollapsed, setIsUnavailableForMonthReportCollapsed] =
    useState(true);
  const volunteerBaseCache = useRef(new Map());
  const randomTiebreakerRef = useRef(new Map()); // usato per randomizzare i pareggi

  const normalizeId = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return value;
    if (typeof value === "string" && value.trim() === "") return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  // Carica gli slot lasciati vuoti manualmente dal localStorage
  const loadManuallyEmptiedSlots = (year, month) => {
    try {
      const stored = localStorage.getItem(
        `manuallyEmptiedSlots_${year}_${month}`
      );
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (error) {
      console.error("Errore nel caricamento degli slot lasciati vuoti:", error);
    }
    return new Set();
  };

  // Salva gli slot lasciati vuoti manualmente nel localStorage
  const saveManuallyEmptiedSlots = (slots, year, month) => {
    try {
      localStorage.setItem(
        `manuallyEmptiedSlots_${year}_${month}`,
        JSON.stringify(Array.from(slots))
      );
    } catch (error) {
      console.error("Errore nel salvataggio degli slot lasciati vuoti:", error);
    }
  };

  const resetLocalCacheState = (persistStorage = true) => {
    setPendingAssignments(new Map());
    setPendingRemovals(new Map());
    const emptySlots = new Set();
    setManuallyEmptiedSlots(emptySlots);

    try {
      const storageKey = `manuallyEmptiedSlots_${selectedMonth.year}_${selectedMonth.month}`;
      if (persistStorage) {
        saveManuallyEmptiedSlots(
          emptySlots,
          selectedMonth.year,
          selectedMonth.month
        );
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.error("Errore durante la pulizia della cache locale:", error);
    }
  };

  // Stati per export
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTarget, setExportTarget] = useState(null); // { postazioneId, postazioneNome } o null per tutte
  const [validatingSnapshot, setValidatingSnapshot] = useState(false);
  const [restoringSnapshot, setRestoringSnapshot] = useState(false);
  const [showSnapshotValidationModal, setShowSnapshotValidationModal] =
    useState(false);
  const [snapshotValidationResult, setSnapshotValidationResult] =
    useState(null);
  const [snapshotValidationPayload, setSnapshotValidationPayload] =
    useState(null);
  const [snapshotValidationFilename, setSnapshotValidationFilename] =
    useState("");
  const [snapshotRestoreResult, setSnapshotRestoreResult] = useState(null);
  const snapshotFileInputRef = useRef(null);

  // Stato per i contatori mensili
  const [contatoriMensili, setContatoriMensili] = useState({});

  const allVolontariForDropdown = useMemo(() => {
    if (!contatoriMensili || Object.keys(contatoriMensili).length === 0) {
      return [];
    }
    return Object.entries(contatoriMensili).map(([id, info]) => ({
      volontario_id: parseInt(id, 10),
      nome: info?.nome || "",
      cognome: info?.cognome || "",
      ultima_assegnazione: info?.ultima_assegnazione || null,
    }));
  }, [contatoriMensili]);

  const hasPendingChanges =
    pendingAssignments.size > 0 || pendingRemovals.size > 0;

  const formatVolunteerFullName = (volunteer) =>
    [volunteer?.nome, volunteer?.cognome].filter(Boolean).join(" ").trim() ||
    "Nome non disponibile";

  const reportSummary = useMemo(
    () => buildMonthlyAvailabilityReport(contatoriMensili),
    [contatoriMensili]
  );

  const hydrateGestioneData = (rawData) => {
    const suspendedSlots = rawData?.suspended_slots || [];
    const suspendedSlotsSet = new Set(
      suspendedSlots
        .filter(
          (item) =>
            item &&
            item.slot_orario_id !== null &&
            item.slot_orario_id !== undefined &&
            item.data
        )
        .map((item) => `${normalizeId(item.slot_orario_id)}-${item.data}`)
    );

    return { ...rawData, suspendedSlotsSet };
  };

  const weeklyConflictIndex = useMemo(() => {
    const index = new Map();

    const addAssignment = (volontarioId, dateValue) => {
      const normalizedId = parseInt(volontarioId, 10);
      if (Number.isNaN(normalizedId)) return;
      const isoDate = toLocalISODate(dateValue);
      if (!isoDate) return;
      const weekKey = getWeekStartISO(isoDate);
      if (!weekKey) return;
      let weekMap = index.get(normalizedId);
      if (!weekMap) {
        weekMap = new Map();
        index.set(normalizedId, weekMap);
      }
      let datesSet = weekMap.get(weekKey);
      if (!datesSet) {
        datesSet = new Set();
        weekMap.set(weekKey, datesSet);
      }
      datesSet.add(isoDate);
    };

    (data?.assegnazioni || []).forEach((assignment) => {
      const assignmentId = assignment.assegnazione_id ?? assignment.id;
      const assignmentVolunteerId = parseInt(assignment.volontario_id, 10);
      if (assignmentId !== null && assignmentId !== undefined) {
        const removedSet = pendingRemovals.get(String(assignmentId));
        if (
          removedSet &&
          !Number.isNaN(assignmentVolunteerId) &&
          removedSet.has(assignmentVolunteerId)
        ) {
          return;
        }
      }
      addAssignment(assignmentVolunteerId, assignment.data_turno);
    });

    pendingAssignments.forEach((assignments) => {
      assignments.forEach((assignment) => {
        addAssignment(
          parseInt(assignment.volontario_id, 10),
          assignment.data_turno || assignment.data
        );
      });
    });

    return index;
  }, [data?.assegnazioni, pendingAssignments, pendingRemovals]);

  const dailyConflictIndex = useMemo(() => {
    const index = new Map(); // volId -> Map(date -> count)

    const addAssignment = (volontarioId, dateValue) => {
      const normalizedId = parseInt(volontarioId, 10);
      if (Number.isNaN(normalizedId)) return;
      const isoDate = toLocalISODate(dateValue);
      if (!isoDate) return;
      let dateMap = index.get(normalizedId);
      if (!dateMap) {
        dateMap = new Map();
        index.set(normalizedId, dateMap);
      }
      const current = dateMap.get(isoDate) || 0;
      dateMap.set(isoDate, current + 1);
    };

    (data?.assegnazioni || []).forEach((assignment) => {
      const assignmentId = assignment.assegnazione_id ?? assignment.id;
      const assignmentVolunteerId = parseInt(assignment.volontario_id, 10);
      if (assignmentId !== null && assignmentId !== undefined) {
        const removedSet = pendingRemovals.get(String(assignmentId));
        if (
          removedSet &&
          !Number.isNaN(assignmentVolunteerId) &&
          removedSet.has(assignmentVolunteerId)
        ) {
          return;
        }
      }
      addAssignment(assignmentVolunteerId, assignment.data_turno);
    });

    pendingAssignments.forEach((assignments) => {
      assignments.forEach((assignment) => {
        addAssignment(
          parseInt(assignment.volontario_id, 10),
          assignment.data_turno || assignment.data
        );
      });
    });

    return index;
  }, [data?.assegnazioni, pendingAssignments, pendingRemovals]);

  const getWeeklyConflictDates = (volontarioId, dateValue) => {
    const normalizedId = parseInt(volontarioId, 10);
    if (Number.isNaN(normalizedId)) return [];
    const isoDate = toLocalISODate(dateValue);
    if (!isoDate) return [];
    const weekKey = getWeekStartISO(isoDate);
    if (!weekKey) return [];
    const weekMap = weeklyConflictIndex.get(normalizedId);
    if (!weekMap) return [];
    const datesSet = weekMap.get(weekKey);
    if (!datesSet || datesSet.size < 2) return [];
    return Array.from(datesSet)
      .filter((value) => value !== isoDate)
      .sort();
  };

  const hasDailyConflict = (volontarioId, dateValue) => {
    const normalizedId = parseInt(volontarioId, 10);
    if (Number.isNaN(normalizedId)) return false;
    const isoDate = toLocalISODate(dateValue);
    if (!isoDate) return false;
    const dateMap = dailyConflictIndex.get(normalizedId);
    if (!dateMap) return false;
    const count = dateMap.get(isoDate) || 0;
    return count > 1;
  };

  const dailyWarningFor = (assignment, date) => {
    const id = Number(assignment.volontario_id);
    const repeated = hasDailyConflict(id, date);
    if (!repeated) return "";
    let reason = assignment.daily_exception_reason;
    if (!reason) {
      const saved = data?.assegnazioni?.find(item => Number(item.volontario_id) === id &&
        toLocalISODate(item.data_turno) === toLocalISODate(date) && item.daily_exception_reason);
      reason = saved?.daily_exception_reason;
      if (!reason) pendingAssignments.forEach(items => {
        const other = items.find(item => Number(item.volontario_id) === id &&
          toLocalISODate(item.data_turno) === toLocalISODate(date) && item.daily_exception_reason);
        if (other) reason = other.daily_exception_reason;
      });
    }
    return getDailyExceptionText(reason, repeated);
  };

  // Carica i contatori mensili di disponibilità e assegnazioni
  const loadContatoriMensili = async () => {
    try {
      const response = await api.get(
        `/disponibilita/contatori-mensili?data_inizio=${selectedDateRange.inizio}&data_fine=${selectedDateRange.fine}`
      );
      setContatoriMensili(response.data);
    } catch (error) {
      console.error("Errore nel caricamento dei contatori mensili:", error);
      // Non mostrare errore all'utente per non interrompere il flusso
      setContatoriMensili({});
    }
  };

  // Carica i dati strutturati per la gestione turni
  const loadGestioneData = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/turni/gestione/${selectedDateRange.inizio}/${selectedDateRange.fine}`
      );
      setData(hydrateGestioneData(response.data));
    } catch (error) {
      console.error("Errore:", error);
      if (error.response?.status !== 401) {
        toastError("Errore di connessione");
      }
    } finally {
      setLoading(false);
    }
  };

  // Funzione per navigare al mese precedente
  const goToPreviousMonth = () => {
    setSelectedMonth((prev) => {
      const newMonth = prev.month === 0 ? 11 : prev.month - 1;
      const newYear = prev.month === 0 ? prev.year - 1 : prev.year;
      const newMonthRange = getMonthRange(newYear, newMonth);
      setSelectedDateRange(newMonthRange);
      return { year: newYear, month: newMonth };
    });
  };

  // Funzione per navigare al mese successivo
  const goToNextMonth = () => {
    setSelectedMonth((prev) => {
      const newMonth = prev.month === 11 ? 0 : prev.month + 1;
      const newYear = prev.month === 11 ? prev.year + 1 : prev.year;
      const newMonthRange = getMonthRange(newYear, newMonth);
      setSelectedDateRange(newMonthRange);
      return { year: newYear, month: newMonth };
    });
  };

  // Funzione per tornare al mese corrente
  const goToCurrentMonth = () => {
    const current = getCurrentMonth();
    const currentRange = getMonthRange(current.year, current.month);
    setSelectedMonth(current);
    setSelectedDateRange(currentRange);
  };

  // Verifica se il mese selezionato è il mese corrente
  const isCurrentMonth = () => {
    const current = getCurrentMonth();
    return (
      selectedMonth.year === current.year &&
      selectedMonth.month === current.month
    );
  };

  useEffect(() => {
    loadGestioneData();
    loadContatoriMensili();
    // Carica gli slot lasciati vuoti manualmente per il mese corrente
    const slotsForMonth = loadManuallyEmptiedSlots(
      selectedMonth.year,
      selectedMonth.month
    );
    setManuallyEmptiedSlots(slotsForMonth);
    // Ricarica i dati quando cambia il range di date
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRange]);

  // Popola/ricostruisce la cache dei volontari quando cambiano anagrafica o contatori
  useEffect(() => {
    const newCache = new Map();

    if (data?.volontari?.length) {
      data.volontari.forEach((v) => {
        const id = parseInt(v.id, 10);
        if (Number.isNaN(id)) return;
        newCache.set(id, {
          volontario_id: id,
          nome: v.nome || "",
          cognome: v.cognome || "",
          sesso: v.sesso || "N/A",
          coniuge_id:
            v.coniuge_id !== undefined && v.coniuge_id !== null
              ? parseInt(v.coniuge_id, 10)
              : null,
        });
      });
    }

    if (contatoriMensili && Object.keys(contatoriMensili).length > 0) {
      Object.entries(contatoriMensili).forEach(([idStr, info]) => {
        const id = parseInt(idStr, 10);
        if (Number.isNaN(id) || newCache.has(id)) return;
        newCache.set(id, {
          volontario_id: id,
          nome: info?.nome || "",
          cognome: info?.cognome || "",
          sesso: info?.sesso || "N/A",
          coniuge_id:
            info?.coniuge_id !== undefined && info?.coniuge_id !== null
              ? parseInt(info.coniuge_id, 10)
              : null,
        });
      });
    }

    volunteerBaseCache.current = newCache;
  }, [data?.volontari, contatoriMensili]);

  // Funzioni di utilità
  const getDayName = (dateString) => {
    const date = new Date(dateString);
    const days = [
      "Domenica",
      "Lunedì",
      "Martedì",
      "Mercoledì",
      "Giovedì",
      "Venerdì",
      "Sabato",
    ];
    return days[date.getDay()];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (timeString) => {
    return timeString.substring(0, 5);
  };

  // Funzione per ottenere il nome del mese
  const getMonthName = (year, month) => {
    const date = new Date(year, month, 1);
    return date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  };

  // Verifica se una postazione è attiva per una data specifica
  const isPostazioneActiveForDate = (postazione, dateString) => {
    // Verifica se la postazione è attiva
    if (postazione.stato !== "attiva") {
      return false;
    }

    if (
      !postazione.giorni_settimana ||
      postazione.giorni_settimana.length === 0
    ) {
      return false;
    }

    const date = new Date(dateString);
    const dayOfWeek = date.getDay(); // 0 = Domenica, 1 = Lunedì, ..., 6 = Sabato

    // Converti in formato nostro sistema (1 = Lunedì, 2 = Martedì, ..., 7 = Domenica)
    // JavaScript: 0=Domenica, 1=Lunedì, ..., 6=Sabato
    // Nostro sistema: 1=Lunedì, 2=Martedì, ..., 7=Domenica
    const nostroGiorno = dayOfWeek === 0 ? 7 : dayOfWeek;

    return postazione.giorni_settimana.includes(nostroGiorno);
  };

  const isSlotSuspendedForDate = (date, slotOrarioId, sourceData = data) => {
    if (!sourceData?.suspendedSlotsSet) return false;
    const normalizedSlotId = normalizeId(slotOrarioId);
    if (normalizedSlotId === null) return false;
    return sourceData.suspendedSlotsSet.has(`${normalizedSlotId}-${date}`);
  };

  const getAvailableVolunteers = (
    date,
    orarioInizio,
    orarioFine,
    slotOrarioId,
    sourceData = data
  ) => {
    if (!sourceData?.disponibilita) return [];

    console.log("🔍 Cercando disponibilità per:", {
      date,
      orarioInizio,
      orarioFine,
      slotOrarioId,
      totalDisponibilita: data.disponibilita.length,
    });

    const targetSlotId = normalizeId(slotOrarioId);

    const filtered = sourceData.disponibilita.filter((d) => {
      // Gestisci correttamente le date senza problemi di timezone
      let disponibilitaDate;
      if (typeof d.data === "string") {
        // Se è già una stringa, prendi solo la parte della data
        disponibilitaDate = d.data.split("T")[0];
      } else {
        // Se è un oggetto Date, converti in formato locale
        disponibilitaDate = d.data.toISOString().split("T")[0];
      }

      const matches =
        disponibilitaDate === date &&
        normalizeId(d.slot_orario_id) === targetSlotId &&
        d.disponibilita_stato === "disponibile" &&
        d.volontario_stato === "attivo";

      if (matches) {
        console.log("✅ Trovata disponibilità:", d.nome, d.cognome);
      }

      return matches;
    });

    console.log("📊 Risultati trovati:", filtered.length);

    // Rimuovo il filtro automatico per gli uomini - lasciamo che sia l'autocompilazione a gestire
    // la logica di "almeno un uomo per turno"
    console.log(
      "👥 Tutti i volontari disponibili (uomini e donne):",
      filtered.length,
      "per",
      date,
      "slot",
      slotOrarioId
    );

    return filtered;
  };

  // Calcola i contatori dinamici per un volontario specifico
  const getContatoriDinamici = (volontarioId, pendingMap = pendingAssignments) => {
    const contatori = contatoriMensili[volontarioId] || {
      disponibilita_totali: 0,
      assegnazioni_totali: 0,
    };

    // Calcola le assegnazioni in sospeso per questo volontario nel mese corrente
    let assegnazioniInSospeso = 0;

    pendingMap.forEach((assignments, _key) => {
      assignments.forEach((assignment) => {
        if (assignment.volontario_id === parseInt(volontarioId)) {
          // Verifica che l'assegnazione sia nel range di date corrente
          const assignmentDate = assignment.data_turno;
          if (
            assignmentDate >= selectedDateRange.inizio &&
            assignmentDate <= selectedDateRange.fine
          ) {
            assegnazioniInSospeso++;
          }
        }
      });
    });

    // Calcola le rimozioni in sospeso per questo volontario nel mese corrente
    let rimozioniInSospeso = 0;

    pendingRemovals.forEach((volontariSet, assegnazioneId) => {
      if (!volontariSet || volontariSet.size === 0) return;
      if (!volontariSet.has(parseInt(volontarioId))) return;

      const assegnazione = data?.assegnazioni?.find(
        (a) => a.assegnazione_id === parseInt(assegnazioneId, 10)
      );
      if (assegnazione) {
        let assegnazioneDate;
        if (typeof assegnazione.data_turno === "string") {
          assegnazioneDate = assegnazione.data_turno.split("T")[0];
        } else {
          assegnazioneDate = assegnazione.data_turno
            .toISOString()
            .split("T")[0];
        }

        if (
          assegnazioneDate >= selectedDateRange.inizio &&
          assegnazioneDate <= selectedDateRange.fine
        ) {
          rimozioniInSospeso++;
        }
      }
    });

    return {
      disponibilita_totali: contatori.disponibilita_totali,
      assegnazioni_totali:
        contatori.assegnazioni_totali +
        assegnazioniInSospeso -
        rimozioniInSospeso,
    };
  };

  // Trova le assegnazioni esistenti per una data, orario e postazione specifici
  // Trova le assegnazioni esistenti per una data e slot orario specifici (incluse modifiche in sospeso)
  const getExistingAssignments = (
    date,
    slotOrarioId,
    postazioneId,
    sourceData = data
  ) => {
    if (!sourceData?.assegnazioni) return [];

    // Assegnazioni dal database
    const slotId = normalizeId(slotOrarioId);
    const postId = normalizeId(postazioneId);

    let assignments = sourceData.assegnazioni.filter((a) => {
      // Gestisci correttamente le date senza problemi di timezone
      let assegnazioneDate;
      if (typeof a.data_turno === "string") {
        assegnazioneDate = a.data_turno.split("T")[0];
      } else {
        assegnazioneDate = a.data_turno.toISOString().split("T")[0];
      }

      return (
        assegnazioneDate === date &&
        normalizeId(a.slot_orario_id) === slotId &&
        normalizeId(a.postazione_id) === postId
      );
    });

    // Rimuovi le assegnazioni che sono state rimosse in sospeso
    assignments = assignments.filter((a) => {
      const removedSet = pendingRemovals.get(String(a.assegnazione_id));
      return !(removedSet && removedSet.has(a.volontario_id));
    });

    return assignments;
  };

  // Usato soltanto per rappresentare lo stato corrente (PDF/CSV e controlli
  // della UI). L'autocompilazione continua a ricevere ed elaborare i dati con
  // il suo flusso invariato.
  const getEffectiveAssignments = (
    date,
    slotOrarioId,
    postazioneId,
    pendingSource = pendingAssignmentsRef.current
  ) => {
    const persistedAssignments = getExistingAssignments(
      date,
      slotOrarioId,
      postazioneId
    );
    const pendingForSlot = getPendingAssignmentsForSlot(
      pendingSource,
      date,
      slotOrarioId,
      postazioneId
    );

    return mergePersistedAndPendingAssignments(
      persistedAssignments,
      pendingForSlot
    );
  };

  const downloadBlobFile = (blob, filename) => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFilenameFromDisposition = (contentDisposition, fallbackFilename) => {
    if (!contentDisposition) {
      return fallbackFilename;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (basicMatch?.[1]) {
      return basicMatch[1];
    }

    return fallbackFilename;
  };

  const extractBlobErrorMessage = async (error, fallbackMessage) => {
    const responseData = error?.response?.data;
    if (responseData instanceof Blob) {
      try {
        const text = await responseData.text();
        const parsed = JSON.parse(text);
        return parsed?.message || fallbackMessage;
      } catch (_blobError) {
        return fallbackMessage;
      }
    }
    return error?.response?.data?.message || fallbackMessage;
  };

  const formatSnapshotIssueDetails = (details) => {
    if (!details) return null;
    try {
      return JSON.stringify(details, null, 2);
    } catch (_error) {
      return String(details);
    }
  };

  // Verifica se una postazione è completamente assegnata per il mese corrente
  const isPostazioneComplete = (postazione) => {
    if (!data?.dateRange || !postazione.slot_orari) {
      return false;
    }

    for (const date of data.dateRange) {
      // Verifica solo le date in cui la postazione è attiva
      if (!isPostazioneActiveForDate(postazione, date)) {
        continue;
      }

      for (const slot of postazione.slot_orari) {
        const existingAssignments = getExistingAssignments(
          date,
          slot.id,
          postazione.id
        );

        const key = `${date}-${slot.id}-${postazione.id}`;
        const pendingAssignmentsForSlot = pendingAssignments.get(key) || [];

        // Filtra le assegnazioni pending che sono state rimosse
        const validPendingAssignments = pendingAssignmentsForSlot.filter(
          (pending) => {
            for (const [assegnazioneId, volontariSet] of pendingRemovals) {
              if (!volontariSet || volontariSet.size === 0) continue;
              const matchingExisting = existingAssignments.find(
                (a) =>
                  a.assegnazione_id === parseInt(assegnazioneId, 10) &&
                  a.volontario_id === pending.volontario_id
              );
              if (matchingExisting && volontariSet.has(pending.volontario_id)) {
                return false;
              }
            }
            return true;
          }
        );

        // Conta le assegnazioni totali (esistenti + pending valide)
        const totalAssignments =
          existingAssignments.length + validPendingAssignments.length;

        const maxProclamatori = postazione.max_proclamatori || 3;

        // Se lo slot è lasciato vuoto manualmente, non conta come incompleto
        if (manuallyEmptiedSlots.has(key)) {
          continue;
        }

        // Se uno slot non è completamente assegnato, la postazione non è completa
        if (totalAssignments < maxProclamatori) {
          return false;
        }
      }
    }

    // Tutti gli slot sono completi
    return true;
  };

  // Verifica se c'è almeno un uomo nelle assegnazioni (esistenti + pending)
  const getVolunteerBaseData = (volontarioId) => {
    const normalizedId = parseInt(volontarioId, 10);
    if (Number.isNaN(normalizedId)) return null;
    const cached = volunteerBaseCache.current.get(normalizedId);
    if (cached) {
      if (!cached.coniuge_id) {
        const upgraded =
          data?.volontari?.find((v) => parseInt(v.id, 10) === normalizedId) ||
          null;
        if (upgraded && upgraded.coniuge_id) {
          const merged = {
            ...cached,
            coniuge_id: parseInt(upgraded.coniuge_id, 10),
          };
          volunteerBaseCache.current.set(normalizedId, merged);
          return merged;
        }
      }
      return cached;
    }
    const fromDisponibilita =
      data?.disponibilita?.find(
        (d) => parseInt(d.volontario_id, 10) === normalizedId
      ) ||
      data?.assegnazioni?.find(
        (a) => parseInt(a.volontario_id, 10) === normalizedId
      );

    // Ordine di priorità: disponibilità/assegnazioni del range, anagrafica completa, contatori mensili.
    const fromVolontariList = data?.volontari?.find(
      (v) => parseInt(v.id, 10) === normalizedId
    );
    const fromContatori = contatoriMensili[normalizedId];

    const baseInfo =
      fromDisponibilita || fromVolontariList || fromContatori || null;

    if (!baseInfo) return null;

    const normalizedBase = {
      volontario_id: normalizedId,
      nome: baseInfo.nome || "",
      cognome: baseInfo.cognome || "",
      sesso: baseInfo.sesso || "N/A",
      coniuge_id:
        baseInfo.coniuge_id !== undefined && baseInfo.coniuge_id !== null
          ? parseInt(baseInfo.coniuge_id, 10)
          : null,
    };

    volunteerBaseCache.current.set(normalizedId, normalizedBase);
    return normalizedBase;
  };

  const hasManInSlot = (
    date,
    slotOrarioId,
    postazioneId,
    sourceData = data,
    pendingMap = pendingAssignments
  ) => {
    // Controlla nelle assegnazioni esistenti
    const existingAssignments = getExistingAssignments(
      date,
      slotOrarioId,
      postazioneId,
      sourceData
    );
    const hasManInExisting = existingAssignments.some((a) => a.sesso === "M");

    if (hasManInExisting) return true;

    // Controlla nelle assegnazioni pending
    const key = `${date}-${slotOrarioId}-${postazioneId}`;
    const pendingAssignmentsForSlot = pendingMap.get(key) || [];
    const normalizedSlotId = normalizeId(slotOrarioId);

    for (const pending of pendingAssignmentsForSlot) {
      const volunteerId = normalizeId(pending.volontario_id);
      if (volunteerId === null) {
        continue;
      }
      // Cerca il sesso del volontario nelle disponibilità
      const volunteerData = sourceData?.disponibilita?.find((d) => {
        return (
          normalizeId(d.volontario_id) === volunteerId &&
          d.data === date &&
          normalizeId(d.slot_orario_id) === normalizedSlotId
        );
      });
      if (volunteerData && volunteerData.sesso === "M") {
        return true;
      }
      const base = getVolunteerBaseData(volunteerId);
      if (base?.sesso === "M") {
        return true;
      }
    }

    return false;
  };

  // Gestisce l'assegnazione manuale di un volontario (salva localmente)
  const handleManualAssignment = (
    date,
    slotOrarioId,
    postazioneId,
    volontarioId,
    forceInsert = false
  ) => {
    console.log("🎯 Aggiungendo volontario:", {
      date,
      slotOrarioId,
      postazioneId,
      volontarioId,
    });

    // Verifica che il volontario sia effettivamente disponibile
    console.log("🔍 Verificando disponibilità per volontario:", volontarioId);
    console.log("📅 Data:", date);
    console.log("⏰ Slot:", slotOrarioId);
    console.log("📊 Disponibilità totali:", data.disponibilita?.length);

    const matchingDisponibilita = data.disponibilita?.filter(
      (d) => d.volontario_id === parseInt(volontarioId)
    );
    console.log(
      "🎯 Disponibilità trovate per questo volontario:",
      matchingDisponibilita
    );

    const isAvailable = data.disponibilita?.some(
      (d) =>
        d.volontario_id === parseInt(volontarioId) &&
        d.data === date &&
        d.slot_orario_id === parseInt(slotOrarioId) &&
        d.disponibilita_stato === "disponibile"
    );

    console.log("✅ Volontario disponibile:", isAvailable);

    if (!isAvailable && !forceInsert) {
      const confirmed = window.confirm(
        'Volontario non disponibile per questo turno, confermi l\'inserimento?'
      );
      if (!confirmed) {
        return;
      }
      return handleManualAssignment(
        date,
        slotOrarioId,
        postazioneId,
        volontarioId,
        true
      );
    }

    // Verifica che il volontario non sia già assegnato nel database
    const existingAssignments = getExistingAssignments(
      date,
      slotOrarioId,
      postazioneId
    );
    const isAlreadyAssigned = existingAssignments.some(
      (a) => a.volontario_id === parseInt(volontarioId)
    );

    if (isAlreadyAssigned) {
      toastError("Il volontario è già assegnato a questo turno nel database");
      return;
    }

    // Verifica anche nelle assegnazioni in sospeso
    const key = `${date}-${slotOrarioId}-${postazioneId}`;
    const currentPendingAssignments = pendingAssignments.get(key) || [];
    const isAlreadyPending = currentPendingAssignments.some(
      (a) => a.volontario_id === parseInt(volontarioId)
    );

    if (isAlreadyPending) {
      toastError("Il volontario è già nelle assegnazioni in sospeso");
      return;
    }

    console.log("🔑 Creata chiave:", key, "con parametri:", {
      date,
      slotOrarioId,
      postazioneId,
    });
    const newPendingAssignments = new Map(pendingAssignments);

    if (!newPendingAssignments.has(key)) {
      newPendingAssignments.set(key, []);
    }

    const assignments = newPendingAssignments.get(key);

    assignments.push({
      volontario_id: parseInt(volontarioId),
      data_turno: date,
      slot_orario_id: slotOrarioId,
      postazione_id: postazioneId,
      override_disponibilita: forceInsert,
    });
    newPendingAssignments.set(key, assignments);

    console.log("📝 DEBUG - Aggiunta assegnazione in sospeso:", {
      key,
      volontarioId,
      assignments: assignments.map((a) => a.volontario_id),
      allPendingAssignments: Array.from(newPendingAssignments.entries()),
    });

    setPendingAssignments(newPendingAssignments);

    // Se questo slot era stato marcato come "lasciato vuoto manualmente",
    // rimuovilo dal Set perché ora l'utente sta aggiungendo volontari
    if (manuallyEmptiedSlots.has(key)) {
      const newManuallyEmptiedSlots = new Set(manuallyEmptiedSlots);
      newManuallyEmptiedSlots.delete(key);
      setManuallyEmptiedSlots(newManuallyEmptiedSlots);
      saveManuallyEmptiedSlots(
        newManuallyEmptiedSlots,
        selectedMonth.year,
        selectedMonth.month
      ); // Salva nel localStorage
      console.log(
        `🗑️ Slot ${key} rimosso da manuallyEmptiedSlots (volontario aggiunto manualmente)`
      );
    }

    // Forza un re-render immediato per mostrare il feedback visivo
    setTimeout(() => {
      setPendingAssignments(new Map(newPendingAssignments));
    }, 0);

    toastSuccess("Volontario aggiunto (modifiche in sospeso)");
  };

  // Rimuove un singolo volontario da un'assegnazione (salva localmente)
  const handleRemoveVolunteer = (assegnazioneId, volontarioId) => {
    console.log("🗑️ Rimuovendo volontario:", { assegnazioneId, volontarioId });

    // Converti assegnazioneId in stringa per poter usare startsWith
    const assegnazioneIdStr = String(assegnazioneId);

    // Se è un'assegnazione in sospeso (inizia con "pending-"), rimuovila dalle assegnazioni in sospeso
    if (assegnazioneIdStr.startsWith("pending-")) {
      const newPendingAssignments = new Map(pendingAssignments);

      // Trova e rimuovi l'assegnazione in sospeso
      for (const [key, assignments] of newPendingAssignments) {
        const filteredAssignments = assignments.filter(
          (a) => a.volontario_id !== parseInt(volontarioId)
        );
        if (filteredAssignments.length !== assignments.length) {
          newPendingAssignments.set(key, filteredAssignments);
          console.log(
            "✅ Rimossa assegnazione in sospeso per volontario:",
            volontarioId
          );
        }
      }

      setPendingAssignments(newPendingAssignments);
      toastSuccess("Volontario rimosso (modifiche in sospeso)");
    } else {
      // Se è un'assegnazione esistente, aggiungila alle rimozioni in sospeso
      const newPendingRemovals = new Map(pendingRemovals);
      const currentSet =
        newPendingRemovals.get(String(assegnazioneId)) || new Set();
      const updatedSet = new Set(currentSet);
      updatedSet.add(parseInt(volontarioId, 10));
      newPendingRemovals.set(String(assegnazioneId), updatedSet);
      setPendingRemovals(newPendingRemovals);

      // Marca questo slot come "lasciato vuoto manualmente" per evitare che l'autocompilazione lo riempia
      // Trova la chiave dello slot basandosi sull'assegnazione
      const assignment = data?.assegnazioni?.find(
        (a) => a.id === parseInt(assegnazioneId)
      );
      if (assignment) {
        let assignmentDate;
        if (typeof assignment.data_turno === "string") {
          assignmentDate = assignment.data_turno.split("T")[0];
        } else {
          assignmentDate = assignment.data_turno.toISOString().split("T")[0];
        }
        const slotKey = `${assignmentDate}-${assignment.slot_orario_id}-${assignment.postazione_id}`;
        const newManuallyEmptiedSlots = new Set(manuallyEmptiedSlots);
        newManuallyEmptiedSlots.add(slotKey);
        setManuallyEmptiedSlots(newManuallyEmptiedSlots);
        saveManuallyEmptiedSlots(
          newManuallyEmptiedSlots,
          selectedMonth.year,
          selectedMonth.month
        ); // Salva nel localStorage
        console.log(
          `🏷️ Slot ${slotKey} marcato come lasciato vuoto manualmente`
        );
      }

      console.log(
        "✅ Aggiunta rimozione in sospeso per assegnazione:",
        assegnazioneId
      );
      toastSuccess("Volontario rimosso (modifiche in sospeso)");
    }
  };

  // Salva tutte le modifiche in sospeso al database
  const savePendingChanges = async () => {
    if (saving) {
      return;
    }

    if (pendingAssignments.size === 0 && pendingRemovals.size === 0) {
      toastInfo("Non ci sono modifiche da salvare");
      return;
    }

    setSaving(true);

    try {
      console.log("💾 Salvando modifiche in sospeso...");
      console.log("📝 Assegnazioni in sospeso:", pendingAssignments);
      console.log("🗑️ Rimozioni in sospeso:", pendingRemovals);

      // Prima rimuovi le rimozioni in sospeso in parallelo
      const removalOperations = [];
      pendingRemovals.forEach((volontariSet, assegnazioneId) => {
        if (!volontariSet || volontariSet.size === 0) {
          return;
        }

        const assegnazioneIdNum = parseInt(assegnazioneId, 10);
        if (Number.isNaN(assegnazioneIdNum)) {
          console.error(
            `❌ ID assegnazione non valido durante la rimozione: ${assegnazioneId}`
          );
          return;
        }

        volontariSet.forEach((volontarioId) => {
          const volontarioIdNum = parseInt(volontarioId, 10);
          if (Number.isNaN(volontarioIdNum)) {
            console.error(
              `❌ ID volontario non valido durante la rimozione: ${volontarioId}`
            );
            return;
          }

          removalOperations.push({
            assegnazioneId: assegnazioneIdNum,
            volontarioId: volontarioIdNum,
          });
        });
      });

      const removalResults = await Promise.allSettled(
        removalOperations.map(({ assegnazioneId, volontarioId }) =>
          api
            .delete(
              `/turni/assegnazione/${assegnazioneId}/volontario/${volontarioId}`
            )
            .then(() => ({ assegnazioneId, volontarioId }))
        )
      );

      removalResults.forEach((result, index) => {
        const info = removalOperations[index];
        if (!info) {
          return;
        }

        if (result.status === "rejected") {
          console.error("❌ Errore rimozione:", {
            assegnazioneId: info.assegnazioneId,
            volontarioId: info.volontarioId,
            error: result.reason?.message,
            response: result.reason?.response?.data,
            status: result.reason?.response?.status,
          });
        } else {
          console.log(
            `✅ Volontario ${info.volontarioId} rimosso da assegnazione ${info.assegnazioneId}`
          );
        }
      });

      const removalErrors = removalResults
        .filter((result) => result.status === "rejected")
        .map((result, index) => ({
          message:
            result.reason?.response?.data?.message ||
            result.reason?.message ||
            "Operazione non riuscita",
          info: removalOperations[index],
        }));

      // Poi aggiungi le nuove assegnazioni (evitando duplicati) in modo concorrente
      const processedVolunteers = new Map(); // key -> Set(volontario_id)
      const assignmentPayloads = [];
      const assignmentsBySlot = new Map();

      for (const [key, assignments] of pendingAssignments) {
        console.log(`📝 Processando assegnazioni per ${key}:`, assignments);

        if (!processedVolunteers.has(key)) {
          processedVolunteers.set(key, new Set());
        }
        const processedForSlot = processedVolunteers.get(key);

        for (const assignment of assignments) {
          const volontarioId = parseInt(assignment.volontario_id, 10);

          if (Number.isNaN(volontarioId)) {
            console.warn(
              `⚠️ Volontario con ID non valido per lo slot ${key}, salto`,
              assignment
            );
            continue;
          }

          if (processedForSlot.has(volontarioId)) {
            console.warn(
              `⚠️ Volontario ${volontarioId} già in coda per questo slot, salto`
            );
            continue;
          }

          const existingAssignments = getExistingAssignments(
            assignment.data_turno,
            assignment.slot_orario_id,
            assignment.postazione_id
          );

          const isAlreadyAssigned = existingAssignments.some(
            (a) => a.volontario_id === volontarioId
          );

          if (isAlreadyAssigned) {
            console.warn(
              `⚠️ Volontario ${volontarioId} già assegnato nel database, salto`
            );
            processedForSlot.add(volontarioId);
            continue;
          }

          let dataTurno = assignment.data_turno;
          if (dataTurno instanceof Date) {
            dataTurno = dataTurno.toISOString().split("T")[0];
          } else if (typeof dataTurno === "string" && dataTurno.includes("T")) {
            dataTurno = dataTurno.split("T")[0];
          }

          const slotOrarioId = parseInt(assignment.slot_orario_id, 10);
          const postazioneId = parseInt(assignment.postazione_id, 10);

          if (Number.isNaN(slotOrarioId) || Number.isNaN(postazioneId)) {
            console.warn(
              `⚠️ Slot o postazione non validi per lo slot ${key}, salto`,
              assignment
            );
            continue;
          }

          const payload = {
            data_turno: dataTurno,
            slot_orario_id: slotOrarioId,
            postazione_id: postazioneId,
            volontario_id: volontarioId,
            override_disponibilita:
              assignment.override_disponibilita === true,
            daily_exception_reason: assignment.daily_exception_reason || null,
          };

          processedForSlot.add(volontarioId);
          const payloadInfo = {
            payload,
            debug: {
              key,
              volontarioId,
              slotOrarioId,
              postazioneId,
              dataTurno,
            },
          };

          assignmentPayloads.push(payloadInfo);

          if (!assignmentsBySlot.has(key)) {
            assignmentsBySlot.set(key, []);
          }
          assignmentsBySlot.get(key).push(payloadInfo);
        }
      }

      const assignmentErrors = [];
      await Promise.all(
        Array.from(assignmentsBySlot.values()).map(async (assignments) => {
          for (const { payload, debug } of assignments) {
            try {
              await api.post("/turni/assegna", payload);
              console.log(
                `✅ Volontario ${debug.volontarioId} assegnato a slot ${debug.slotOrarioId}/${debug.postazioneId} per il ${debug.dataTurno}`
              );
            } catch (error) {
              assignmentErrors.push({ error, debug });
              console.error("❌ Errore assegnazione volontario:", {
                ...debug,
                error: error.message,
                response: error.response?.data,
                status: error.response?.status,
              });
            }
          }
        })
      );

      if (removalErrors.length > 0 || assignmentErrors.length > 0) {
        const combinedErrors = [
          ...removalErrors.map((err) => err.message),
          ...assignmentErrors.map(
            ({ error }) =>
              error.response?.data?.message ||
              error.message ||
              "Operazione non riuscita"
          ),
        ];

        throw new Error(
          combinedErrors.length === 1
            ? combinedErrors[0]
            : `${combinedErrors.length} operazioni non sono state completate`
        );
      }

      // Salva gli slot lasciati vuoti manualmente PRIMA di pulire pendingRemovals
      // così possiamo mantenerli dopo il salvataggio
      const slotsToKeepAsEmpty = new Set(manuallyEmptiedSlots);

      // Pulisci le modifiche in sospeso
      setPendingAssignments(new Map());
      setPendingRemovals(new Map());

      // Ricarica i dati per aggiornare la visualizzazione
      await loadGestioneData();
      await loadContatoriMensili();

      // Ripristina gli slot lasciati vuoti manualmente dopo il ricaricamento
      // così l'autocompilazione continuerà a rispettarli
      setManuallyEmptiedSlots(slotsToKeepAsEmpty);
      saveManuallyEmptiedSlots(
        slotsToKeepAsEmpty,
        selectedMonth.year,
        selectedMonth.month
      ); // Salva nel localStorage

      toastSuccess("Modifiche salvate con successo");
    } catch (error) {
      console.error("Errore nel salvataggio:", {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack,
      });
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Errore nel salvataggio delle modifiche";
      toastError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSendNotifications = async () => {
    if (saving || compiling) {
      return;
    }
    if (pendingAssignments.size > 0 || pendingRemovals.size > 0) {
      toastInfo("Salva prima le modifiche per inviare le notifiche");
      return;
    }
    setNotifyLoading(true);
    try {
      const payload = {
        data_inizio: selectedDateRange.inizio,
        data_fine: selectedDateRange.fine,
      };
      const { data: response } = await api.post(
        "/push/assignments/send",
        payload
      );
      toastSuccess(
        response?.message ||
          `Notifiche inviate (${response?.sent || 0} inviate)`
      );
    } catch (error) {
      console.error("Errore invio notifiche assegnazioni:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante l'invio delle notifiche"
      );
    } finally {
      setNotifyLoading(false);
    }
  };

  const normalizeAssignmentDateValue = (value) =>
    value ? new Date(value).setHours(0, 0, 0, 0) : 0;

  const getRandomTiebreaker = (id) => {
    const key = id ?? `anon-${Math.random()}`;
    if (!randomTiebreakerRef.current.has(key)) {
      randomTiebreakerRef.current.set(key, Math.random());
    }
    return randomTiebreakerRef.current.get(key);
  };


  const sortManualSelectionList = (list) =>
    list.sort((a, b) => {
      const statsA = getContatoriDinamici(a.volontario_id);
      const statsB = getContatoriDinamici(b.volontario_id);
      if (statsA.assegnazioni_totali !== statsB.assegnazioni_totali) {
        return statsA.assegnazioni_totali - statsB.assegnazioni_totali;
      }
      const lastA = a.ultima_assegnazione
        ? normalizeAssignmentDateValue(a.ultima_assegnazione)
        : Number.POSITIVE_INFINITY;
      const lastB = b.ultima_assegnazione
        ? normalizeAssignmentDateValue(b.ultima_assegnazione)
        : Number.POSITIVE_INFINITY;
      if (lastA !== lastB) {
        return lastA - lastB;
      }
      return getRandomTiebreaker(a.volontario_id) - getRandomTiebreaker(b.volontario_id);
    });

  const buildNonAvailableVolunteers = (
    availableIds = new Set(),
    excludedIds = new Set()
  ) => {
    if (!allVolontariForDropdown || allVolontariForDropdown.length === 0) {
      return [];
    }
    return allVolontariForDropdown.filter((vol) => {
      const id = vol.volontario_id;
      return !availableIds.has(id) && !excludedIds.has(id);
    });
  };

  const isWithinAutocompileScope = (dateValue, postazioneValue, targetPostazioneId = null) => {
    const isoDate = toLocalISODate(dateValue);
    if (!isoDate) return false;
    if (
      isoDate < selectedDateRange.inizio ||
      isoDate > selectedDateRange.fine
    ) {
      return false;
    }
    if (targetPostazioneId === null || targetPostazioneId === undefined) {
      return true;
    }
    return parseInt(postazioneValue, 10) === parseInt(targetPostazioneId, 10);
  };

  const stripPendingAssignmentsForScope = (
    sourcePendingAssignments,
    targetPostazioneId = null
  ) => {
    const filtered = new Map();

    sourcePendingAssignments.forEach((assignments, key) => {
      const remainingAssignments = assignments.filter(
        (assignment) =>
          !isWithinAutocompileScope(
            assignment.data_turno || assignment.data,
            assignment.postazione_id,
            targetPostazioneId
          )
      );

      if (remainingAssignments.length > 0) {
        filtered.set(key, remainingAssignments);
      }
    });

    return filtered;
  };

  const stripPendingRemovalsForScope = (
    sourcePendingRemovals,
    targetPostazioneId = null,
    sourceData = data
  ) => {
    const filtered = new Map();

    sourcePendingRemovals.forEach((volontariSet, assegnazioneId) => {
      if (!volontariSet || volontariSet.size === 0) {
        return;
      }

      const matchingAssignments =
        sourceData?.assegnazioni?.filter((assignment) => {
          const assignmentId =
            assignment.assegnazione_id ?? assignment.id ?? null;
          return parseInt(assignmentId, 10) === parseInt(assegnazioneId, 10);
        }) || [];

      const isInScope = matchingAssignments.some((assignment) =>
        isWithinAutocompileScope(
          assignment.data_turno,
          assignment.postazione_id,
          targetPostazioneId
        )
      );

      if (!isInScope) {
        filtered.set(assegnazioneId, new Set(volontariSet));
      }
    });

    return filtered;
  };

  const serializePendingAssignmentsForPreview = (pendingMap) => {
    const items = [];
    pendingMap.forEach((assignments) => {
      assignments.forEach((assignment) => {
        let dataTurno = assignment.data_turno;
        if (dataTurno instanceof Date) {
          dataTurno = dataTurno.toISOString().split("T")[0];
        } else if (typeof dataTurno === "string" && dataTurno.includes("T")) {
          dataTurno = dataTurno.split("T")[0];
        }

        items.push({
          volontario_id: parseInt(assignment.volontario_id, 10),
          data_turno: dataTurno,
          slot_orario_id: parseInt(assignment.slot_orario_id, 10),
          postazione_id: parseInt(assignment.postazione_id, 10),
          nome: assignment.nome || "",
          cognome: assignment.cognome || "",
          sesso: assignment.sesso || "",
          coniuge_id:
            assignment.coniuge_id !== undefined && assignment.coniuge_id !== null
              ? parseInt(assignment.coniuge_id, 10)
              : null,
          override_disponibilita:
            assignment.override_disponibilita === true,
          daily_exception_reason: assignment.daily_exception_reason || null,
        });
      });
    });
    return items;
  };

  const serializePendingRemovalsForPreview = (sourcePendingRemovals = pendingRemovals) => {
    const items = [];
    sourcePendingRemovals.forEach((volontariSet, assegnazioneId) => {
      if (!volontariSet || volontariSet.size === 0) {
        return;
      }
      volontariSet.forEach((volontarioId) => {
        items.push({
          assegnazione_id: parseInt(assegnazioneId, 10),
          volontario_id: parseInt(volontarioId, 10),
        });
      });
    });
    return items;
  };

  // Esegue l'autocompilazione automatica per postazione specifica
  const executeAutocompilazione = async (
    postazioneId = null,
    postazioneNome = null
  ) => {
    const isSpecificAutocompilazione = postazioneId !== null;
    // Rigenera i tie-breaker random per ogni esecuzione, così chi ha stessi conteggi non resta sempre in testa
    randomTiebreakerRef.current = new Map();

    setResettingInfo({
      label:
        postazioneId !== null
          ? `Autocompilazione di "${postazioneNome}"`
          : "Autocompilazione di tutte le postazioni",
      description:
        "Sto liberando gli slot di questa postazione e aggiornando le ultime assegnazioni",
    });
    setCompiling(true);
    try {
      console.log(
        "🚀 Inizio autocompilazione per:",
        postazioneNome || "tutte le postazioni"
      );

      // Aggiorna i dati prima di procedere per evitare assegnazioni con disponibilità obsolete
      let currentData = data;
      try {
        const latestDataResponse = await api.get(
          `/turni/gestione/${selectedDateRange.inizio}/${selectedDateRange.fine}`
        );
        currentData = hydrateGestioneData(latestDataResponse.data);
        setData(currentData);
      } catch (refreshError) {
        console.warn(
          "⚠️ Impossibile aggiornare i dati prima dell'autocompilazione, si utilizzano quelli in cache",
          refreshError
        );
        toastInfo(
          "Non è stato possibile aggiornare le disponibilità dal server. Vengono usati i dati in cache."
        );
      }

      const basePendingAssignments = stripPendingAssignmentsForScope(
        pendingAssignments,
        postazioneId
      );
      const basePendingRemovals = stripPendingRemovalsForScope(
        pendingRemovals,
        postazioneId,
        currentData
      );

      const previewResponse = await api.post("/turni/autocompilazione", {
        data_inizio: selectedDateRange.inizio,
        data_fine: selectedDateRange.fine,
        congregazione_id: activeCongregazione?.id ?? undefined,
        postazione_id: postazioneId ?? undefined,
        preview: true,
        draft_pending_assignments:
          serializePendingAssignmentsForPreview(basePendingAssignments),
        draft_pending_removals: serializePendingRemovalsForPreview(
          basePendingRemovals
        ),
        manually_emptied_slot_keys: Array.from(manuallyEmptiedSlots),
      });

      const previewData = previewResponse?.data || previewResponse;
      const previewAssignments = Array.isArray(previewData.preview_assignments)
        ? previewData.preview_assignments
        : [];
      const newPendingAssignments = new Map(basePendingAssignments);

      previewAssignments.forEach((assignment) => {
        const dataTurno =
          typeof assignment.data_turno === "string"
            ? assignment.data_turno.split("T")[0]
            : assignment.data_turno;
        const slotOrarioId = parseInt(assignment.slot_orario_id, 10);
        const targetPostazioneId = parseInt(assignment.postazione_id, 10);
        const volontarioId = parseInt(assignment.volontario_id, 10);

        if (
          !dataTurno ||
          Number.isNaN(slotOrarioId) ||
          Number.isNaN(targetPostazioneId) ||
          Number.isNaN(volontarioId)
        ) {
          return;
        }

        const key = `${dataTurno}-${slotOrarioId}-${targetPostazioneId}`;
        const existingPendingForSlot = newPendingAssignments.get(key) || [];
        const alreadyPending = existingPendingForSlot.some(
          (item) => parseInt(item.volontario_id, 10) === volontarioId
        );
        const alreadyAssignedInDb = getExistingAssignments(
          dataTurno,
          slotOrarioId,
          targetPostazioneId,
          currentData
        ).some((item) => parseInt(item.volontario_id, 10) === volontarioId);

        if (alreadyPending || alreadyAssignedInDb) {
          return;
        }

        existingPendingForSlot.push({
          volontario_id: volontarioId,
          data_turno: dataTurno,
          slot_orario_id: slotOrarioId,
          postazione_id: targetPostazioneId,
          nome: assignment.nome || "",
          cognome: assignment.cognome || "",
          sesso: assignment.sesso || "N/A",
          daily_exception_reason: assignment.daily_exception_reason || null,
          coniuge_id:
            assignment.coniuge_id !== undefined && assignment.coniuge_id !== null
              ? parseInt(assignment.coniuge_id, 10)
              : null,
        });
        newPendingAssignments.set(key, existingPendingForSlot);
      });

      setPendingAssignments(newPendingAssignments);
      setPendingRemovals(basePendingRemovals);

      let totalNewAssignments = 0;
      newPendingAssignments.forEach((assignments) => {
        assignments.forEach((assignment) => {
          if (
            isWithinAutocompileScope(
              assignment.data_turno,
              assignment.postazione_id,
              postazioneId
            )
          ) {
            totalNewAssignments += 1;
          }
        });
      });

      const successMessage = isSpecificAutocompilazione
        ? `Autocompilazione completata per "${postazioneNome}" nel mese ${getMonthName(
            selectedMonth.year,
            selectedMonth.month
          )}: ${totalNewAssignments} assegnazioni in bozza ricalcolate`
        : `Autocompilazione completata per ${getMonthName(
            selectedMonth.year,
            selectedMonth.month
          )}: ${totalNewAssignments} assegnazioni in bozza ricalcolate`;

      toastSuccess(successMessage);

      if (previewData.warning === "nessuna_disponibilita_delegati") {
        toastInfo(previewData.message || "Nessuna disponibilità utile trovata.");
      }

      if (totalNewAssignments > 0) {
        toastInfo("Clicca 'Salva Modifiche' per confermare le assegnazioni", {
          icon: "💾",
          duration: 4000,
        });
      } else {
        toastInfo(
          "Nessuna nuova assegnazione necessaria. Tutti gli slot vuoti/incompleti sono stati completati.",
          {
            icon: "✅",
            duration: 3000,
          }
        );
      }
    } catch (error) {
      console.error("❌ Errore nell'autocompilazione:", error);
      toastError(`Errore nell'autocompilazione: ${error.message}`);
    } finally {
      setCompiling(false);
      setResettingInfo(null);
    }
  };

  const handleReset = async () => {
    const confirmMessage = `Sei sicuro di voler eliminare tutte le assegnazioni del mese ${getMonthName(
      selectedMonth.year,
      selectedMonth.month
    )}?\n\nQuesta azione pulira completamente tutte le postazioni della congregazione aperta e annullera anche tutte le modifiche locali in sospeso.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setResettingInfo({
      label: "Pulizia completa del mese...",
      description:
        "Pulizia completa in corso. Stiamo azzerando assegnazioni e bozza locale del mese selezionato.",
    });
    setCompiling(true);
    try {
      const result = await api.post("/turni/reset", {
        data_inizio: selectedDateRange.inizio,
        data_fine: selectedDateRange.fine,
        congregazione_id: activeCongregazione?.id ?? undefined,
      });

      const resultData = result?.data || result;
      const successMessage = `Reset completo del mese ${getMonthName(
        selectedMonth.year,
        selectedMonth.month
      )}: ${resultData.assegnazioni_eliminate || 0} assegnazioni eliminate`;

      toastSuccess(successMessage);
      resetLocalCacheState(true);
      await Promise.all([loadGestioneData(), loadContatoriMensili()]);
    } catch (error) {
      console.error("Errore:", error);
      toastError(
        error.response?.data?.message ||
          error.message ||
          "Errore di connessione"
      );
    } finally {
      setCompiling(false);
      setResettingInfo(null);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      resetLocalCacheState(false);
      await Promise.all([loadGestioneData(), loadContatoriMensili()]);
      toastSuccess("Cache turni cancellata e dati aggiornati.");
    } catch (error) {
      console.error("Errore durante la cancellazione della cache turni:", error);
      toastError(
        error.response?.data?.message ||
          error.message ||
          "Impossibile cancellare la cache in questo momento"
      );
    } finally {
      setClearingCache(false);
    }
  };

  // Funzioni per l'export delle tabelle mensili
  const checkIncompleteAssignments = (
    postazioneId = null,
    pendingSource = pendingAssignmentsRef.current
  ) => {
    const incompleteSlots = [];

    data?.postazioni?.forEach((postazione) => {
      if (postazioneId && postazione.id !== postazioneId) return;

      data?.dateRange?.forEach((date) => {
        if (!isPostazioneActiveForDate(postazione, date)) return;

        postazione.slot_orari?.forEach((slot) => {
          const effectiveAssignments = getEffectiveAssignments(
            date,
            slot.id,
            postazione.id,
            pendingSource
          );
          const maxProclamatori = postazione.max_proclamatori || 3;

          if (
            effectiveAssignments.length > 0 &&
            effectiveAssignments.length < maxProclamatori
          ) {
            incompleteSlots.push({
              postazione: postazione.luogo,
              data: formatDate(date),
              orario: `${formatTime(slot.orario_inizio)} - ${formatTime(
                slot.orario_fine
              )}`,
              assegnati: effectiveAssignments.length,
              max: maxProclamatori,
            });
          }
        });
      });
    });

    return incompleteSlots;
  };

  const exportToPDF = async (
    postazioneId = null,
    postazioneNome = null,
    pendingSource = exportDraftSnapshotRef.current
  ) => {
    setExporting(true);

    try {
      const isSpecificExport = postazioneId !== null;
      const incompleteSlots = checkIncompleteAssignments(
        postazioneId,
        pendingSource
      );

      if (incompleteSlots.length > 0) {
        const confirmMessage = `Alcuni turni non sono completamente assegnati:\n\n${incompleteSlots
          .slice(0, 3)
          .map(
            (slot) =>
              `• ${slot.postazione} - ${slot.data} ${slot.orario} (${slot.assegnati}/${slot.max})`
          )
          .join("\n")}${
          incompleteSlots.length > 3
            ? `\n... e altri ${incompleteSlots.length - 3} turni`
            : ""
        }\n\nVuoi davvero esportare la tabella?`;

        if (!window.confirm(confirmMessage)) {
          setExporting(false);
          return;
        }
      }

      const JsPDF = await loadJsPdf();
      const doc = new JsPDF("portrait", "mm", "a4");
      registerMontserrat(doc);
      doc.setFont(FONT_MONTSERRAT, "normal");
      const pageWidth = doc.internal.pageSize.getWidth();

      // Titolo del documento
      const title = isSpecificExport
        ? `Turni Mensili - ${postazioneNome}`
        : `Turni Mensili - Tutte le Postazioni`;
      const subtitle = `${getMonthName(
        selectedMonth.year,
        selectedMonth.month
      )}`;

      doc.setFontSize(18);
      doc.setFont(FONT_MONTSERRAT, "bold");
      doc.text(title, pageWidth / 2, 20, { align: "center" });

      doc.setFontSize(14);
      doc.setFont(FONT_MONTSERRAT, "normal");
      doc.text(subtitle, pageWidth / 2, 30, { align: "center" });

      // Prepara i dati per la tabella
      const tableData = [];
      const rowSuspendedFlags = [];
      const headers = [
        "Postazione",
        "Data",
        "Giorno",
        "Orario",
        "Volontari Assegnati",
        "Max",
      ];

      const sortedDates = getMonthlyExportDates(data?.dateRange);

      data?.postazioni?.forEach((postazione) => {
        postazione._sortableName = (postazione.luogo || "").toLowerCase();
      });

      sortedDates.forEach((date) => {
        const activePostazioni = (data?.postazioni || [])
          .filter((p) => (!postazioneId ? true : p.id === postazioneId))
          .filter((p) => isPostazioneActiveForDate(p, date))
          .sort((a, b) => (a._sortableName || "").localeCompare(b._sortableName || ""));

        activePostazioni.forEach((postazione) => {
          (postazione.slot_orari || [])
            .slice()
            .sort(
              (a, b) =>
                Number(a.orario_inizio?.replace(":", "")) -
                Number(b.orario_inizio?.replace(":", ""))
            )
            .forEach((slot) => {
              const effectiveAssignments = getEffectiveAssignments(
                date,
                slot.id,
                postazione.id,
                pendingSource
              );
              const slotSuspended = isSlotSuspendedForDate(date, slot.id);
              const maxProclamatori = postazione.max_proclamatori || 3;

              const volontari =
                slotSuspended
                  ? "Turno annullato"
                  : formatAssignmentsForExport(
                      effectiveAssignments,
                      getVolunteerBaseData
                    );

              tableData.push([
                postazione.luogo,
                formatDate(date),
                getDayName(date),
                `${formatTime(slot.orario_inizio)} - ${formatTime(
                  slot.orario_fine
                )}`,
                volontari,
                maxProclamatori,
              ]);
              rowSuspendedFlags.push(slotSuspended);
            });
        });
      });

      // Genera la tabella
      doc.autoTable({
        head: [headers],
        body: tableData,
        startY: 40,
        rowPageBreak: "avoid",
        styles: {
          font: FONT_MONTSERRAT,
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          font: FONT_MONTSERRAT,
          fillColor: [59, 130, 246], // Blue-500
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252], // Gray-50
        },
        columnStyles: {
          0: { cellWidth: 30 }, // Postazione
          1: { cellWidth: 20 }, // Data
          2: { cellWidth: 20 }, // Giorno
          3: { cellWidth: 25 }, // Orario
          4: { cellWidth: 70 }, // Volontari
          5: { cellWidth: 15 }, // Max
        },
        didParseCell: function (hookData) {
          if (hookData.section === "body") {
            const rowIndex = hookData.row.index;
            if (rowSuspendedFlags[rowIndex]) {
              hookData.cell.styles.fillColor = [239, 68, 68]; // Red-500
              hookData.cell.styles.textColor = 255;
            }
          }
        },
      });

      // Footer con informazioni
      const finalY = doc.lastAutoTable.finalY || 40;
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.text(
        `Esportato il ${new Date().toLocaleDateString(
          "it-IT"
        )} alle ${new Date().toLocaleTimeString("it-IT")}`,
        pageWidth / 2,
        finalY + 10,
        { align: "center" }
      );

      // Salva il PDF
      const filename = isSpecificExport
        ? `turni_${postazioneNome.replace(/\s+/g, "_")}_${
            selectedMonth.year
          }_${String(selectedMonth.month + 1).padStart(2, "0")}.pdf`
        : `turni_tutte_postazioni_${selectedMonth.year}_${String(
            selectedMonth.month + 1
          ).padStart(2, "0")}.pdf`;

      doc.save(filename);
      toastSuccess(`PDF esportato: ${filename}`);
    } catch (error) {
      console.error("Errore export PDF:", error);
      toastError("Errore durante l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async (
    postazioneId = null,
    postazioneNome = null,
    pendingSource = exportDraftSnapshotRef.current
  ) => {
    setExporting(true);

    try {
      const isSpecificExport = postazioneId !== null;
      const incompleteSlots = checkIncompleteAssignments(
        postazioneId,
        pendingSource
      );

      if (incompleteSlots.length > 0) {
        const confirmMessage = `Alcuni turni non sono completamente assegnati:\n\n${incompleteSlots
          .slice(0, 3)
          .map(
            (slot) =>
              `• ${slot.postazione} - ${slot.data} ${slot.orario} (${slot.assegnati}/${slot.max})`
          )
          .join("\n")}${
          incompleteSlots.length > 3
            ? `\n... e altri ${incompleteSlots.length - 3} turni`
            : ""
        }\n\nVuoi davvero esportare la tabella?`;

        if (!window.confirm(confirmMessage)) {
          setExporting(false);
          return;
        }
      }

      // Prepara i dati per Excel
      const excelData = [];
      const headers = [
        "Postazione",
        "Indirizzo",
        "Data",
        "Giorno",
        "Orario",
        "Volontari Assegnati",
        "Max Proclamatori",
        "Stato",
        "Note",
      ];

      data?.postazioni?.forEach((postazione) => {
        if (postazioneId && postazione.id !== postazioneId) return;

        data?.dateRange?.forEach((date) => {
          if (!isPostazioneActiveForDate(postazione, date)) return;

          postazione.slot_orari?.forEach((slot) => {
            const effectiveAssignments = getEffectiveAssignments(
              date,
              slot.id,
              postazione.id,
              pendingSource
            );
            const maxProclamatori = postazione.max_proclamatori || 3;
            const isFullyAssigned =
              effectiveAssignments.length >= maxProclamatori;
            const isPartiallyAssigned = effectiveAssignments.length > 0;

            let stato = "Vuoto";
            if (isFullyAssigned) stato = "Completo";
            else if (isPartiallyAssigned) stato = "Parziale";

            const volontari = formatAssignmentsForExport(
              effectiveAssignments,
              getVolunteerBaseData
            );

            let note = "";
            if (isPartiallyAssigned && !isFullyAssigned) {
              note = `Mancano ${
                maxProclamatori - effectiveAssignments.length
              } volontari`;
            }

            excelData.push([
              postazione.luogo,
              postazione.indirizzo,
              formatDate(date),
              getDayName(date),
              `${formatTime(slot.orario_inizio)} - ${formatTime(
                slot.orario_fine
              )}`,
              volontari,
              maxProclamatori,
              stato,
              note,
            ]);
          });
        });
      });

      // Crea il contenuto CSV
      const csvContent = [
        headers.join(","),
        ...excelData.map((row) =>
          row
            .map((cell) =>
              typeof cell === "string" && cell.includes(",")
                ? `"${cell.replace(/"/g, '""')}"`
                : cell
            )
            .join(",")
        ),
      ].join("\n");

      // Crea e scarica il file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      const filename = isSpecificExport
        ? `turni_${postazioneNome.replace(/\s+/g, "_")}_${
            selectedMonth.year
          }_${String(selectedMonth.month + 1).padStart(2, "0")}.csv`
        : `turni_tutte_postazioni_${selectedMonth.year}_${String(
            selectedMonth.month + 1
          ).padStart(2, "0")}.csv`;

      downloadBlobFile(blob, filename);

      toastSuccess(`Excel esportato: ${filename}`);
    } catch (error) {
      console.error("Errore export Excel:", error);
      toastError("Errore durante l'export Excel");
    } finally {
      setExporting(false);
    }
  };

  const exportToJsonBackup = async (
    postazioneId = null,
    postazioneNome = null
  ) => {
    setExporting(true);

    try {
      if (pendingAssignments.size > 0 || pendingRemovals.size > 0) {
        toastInfo(
          "Salva prima le modifiche per esportare uno snapshot coerente del database."
        );
        return;
      }

      const isSpecificExport = postazioneId !== null;
      const query = new URLSearchParams();
      if (postazioneId) {
        query.set("postazione_id", postazioneId);
      }

      const url = `/turni/snapshot/export/${selectedDateRange.inizio}/${selectedDateRange.fine}${
        query.toString() ? `?${query.toString()}` : ""
      }`;
      const response = await api.get(url, { responseType: "blob" });

      const fallbackFilename = isSpecificExport
        ? `snapshot_turni_${postazioneNome.replace(/\s+/g, "_")}_${
            selectedDateRange.inizio
          }_${selectedDateRange.fine}.json`
        : `snapshot_turni_tutte_postazioni_${selectedDateRange.inizio}_${selectedDateRange.fine}.json`;
      const filename = getFilenameFromDisposition(
        response.headers?.["content-disposition"],
        fallbackFilename
      );

      downloadBlobFile(response.data, filename);
      toastSuccess(`Snapshot JSON esportato: ${filename}`);
    } catch (error) {
      console.error("Errore export snapshot JSON:", error);
      const message = await extractBlobErrorMessage(
        error,
        "Errore durante l'export snapshot JSON"
      );
      toastError(message);
    } finally {
      setExporting(false);
    }
  };

  // Funzioni per l'export unificato
  const openExportModal = (postazioneId = null, postazioneNome = null) => {
    exportDraftSnapshotRef.current = cloneAssignmentDrafts(pendingAssignments);
    setExportTarget(postazioneId ? { postazioneId, postazioneNome } : null);
    setShowExportModal(true);
  };

  const closeExportModal = () => {
    setShowExportModal(false);
    setExportTarget(null);
  };

  const openSnapshotValidationModal = () => {
    setShowSnapshotValidationModal(true);
  };

  const closeSnapshotValidationModal = () => {
    if (validatingSnapshot || restoringSnapshot) {
      return;
    }
    setShowSnapshotValidationModal(false);
    setSnapshotValidationResult(null);
    setSnapshotValidationPayload(null);
    setSnapshotValidationFilename("");
    setSnapshotRestoreResult(null);
    if (snapshotFileInputRef.current) {
      snapshotFileInputRef.current.value = "";
    }
  };

  const triggerSnapshotValidationPicker = () => {
    openSnapshotValidationModal();
    setSnapshotValidationResult(null);
    setSnapshotValidationPayload(null);
    setSnapshotValidationFilename("");
    setSnapshotRestoreResult(null);
    window.setTimeout(() => {
      snapshotFileInputRef.current?.click();
    }, 0);
  };

  const handleSnapshotFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      if (!snapshotValidationResult) {
        setShowSnapshotValidationModal(false);
      }
      return;
    }

    setShowSnapshotValidationModal(true);
    setSnapshotValidationFilename(file.name);
    setSnapshotValidationResult(null);
    setSnapshotValidationPayload(null);
    setSnapshotRestoreResult(null);
    setValidatingSnapshot(true);

    try {
      const fileContent = await file.text();
      let parsedSnapshot;

      try {
        parsedSnapshot = JSON.parse(fileContent);
      } catch (_parseError) {
        throw new Error("Il file selezionato non contiene un JSON valido.");
      }

      setSnapshotValidationPayload(parsedSnapshot);
      const response = await api.post("/turni/snapshot/validate", {
        snapshot: parsedSnapshot,
      });

      setSnapshotValidationResult(response.data);

      if (response.data?.valid) {
        toastSuccess("Snapshot valido. Nessun errore bloccante rilevato.");
      } else {
        toastInfo(
          "Validazione completata: il file contiene errori bloccanti o warning."
        );
      }
    } catch (error) {
      console.error("Errore validazione snapshot:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.details?.[0] ||
        error?.message ||
        "Errore durante la validazione dello snapshot";
      if (!error?.response) {
        setSnapshotValidationPayload(null);
      }
      setSnapshotValidationResult({
        valid: false,
        blocking_errors: [
          {
            code: "snapshot_validation_request_failed",
            message,
          },
        ],
        warnings: [],
        summary: null,
        resolved_scope: null,
      });
      toastError(message);
    } finally {
      setValidatingSnapshot(false);
      if (snapshotFileInputRef.current) {
        snapshotFileInputRef.current.value = "";
      }
    }
  };

  const hasPendingTurnChanges =
    pendingAssignments.size > 0 || pendingRemovals.size > 0;
  const snapshotScopeMatchesCurrentRange =
    snapshotValidationResult?.resolved_scope?.data_inizio ===
      selectedDateRange.inizio &&
    snapshotValidationResult?.resolved_scope?.data_fine ===
      selectedDateRange.fine;
  const canRestoreValidatedSnapshot = Boolean(
    snapshotValidationResult?.valid &&
      snapshotValidationPayload &&
      snapshotScopeMatchesCurrentRange &&
      !hasPendingTurnChanges &&
      !validatingSnapshot &&
      !restoringSnapshot &&
      !saving &&
      !compiling
  );

  const handleRestoreSnapshot = async () => {
    if (!snapshotValidationResult?.valid || !snapshotValidationPayload) {
      toastInfo("Valida prima uno snapshot senza errori bloccanti.");
      return;
    }

    if (hasPendingTurnChanges) {
      toastInfo(
        "Salva o annulla prima le modifiche locali in sospeso prima di eseguire il restore."
      );
      return;
    }

    if (!snapshotScopeMatchesCurrentRange) {
      toastInfo(
        "Per sicurezza apri prima in pagina lo stesso mese dello snapshot da ripristinare."
      );
      return;
    }

    const warningCount = snapshotValidationResult.warnings?.length || 0;
    const scope = snapshotValidationResult.resolved_scope;
    const confirmed = window.confirm(
      `Questo restore sostituira completamente i dati operativi del periodo ${scope.data_inizio} - ${scope.data_fine}${
        scope.postazione_id ? ` per la postazione ${scope.postazione_id}` : ""
      }.\n\nSaranno ripristinate disponibilita, slot sospesi e assegnazioni del file snapshot.\n\nWarning attuali: ${warningCount}.\n\nVuoi procedere?`
    );

    if (!confirmed) {
      return;
    }

    setRestoringSnapshot(true);
    try {
      const response = await api.post("/turni/snapshot/restore", {
        snapshot: snapshotValidationPayload,
        mode: "replace_range",
        snapshot_hash: snapshotValidationResult.snapshot_hash,
        confirm_replace_range: true,
      });

      setSnapshotRestoreResult(response.data);
      resetLocalCacheState(true);
      await Promise.all([loadGestioneData(), loadContatoriMensili()]);
      toastSuccess("Snapshot ripristinato con successo.");
    } catch (error) {
      console.error("Errore restore snapshot:", error);
      const responseData = error?.response?.data;
      if (responseData?.blocking_errors || responseData?.warnings) {
        setSnapshotValidationResult({
          valid: responseData.valid ?? false,
          blocking_errors: responseData.blocking_errors || [],
          warnings: responseData.warnings || [],
          summary: responseData.summary || null,
          resolved_scope: responseData.resolved_scope || null,
          snapshot_hash:
            responseData.snapshot_hash || snapshotValidationResult?.snapshot_hash,
        });
      }
      setSnapshotRestoreResult(null);
      toastError(
        responseData?.message || "Errore durante il restore dello snapshot"
      );
    } finally {
      setRestoringSnapshot(false);
    }
  };

  const handleExport = async (format) => {
    if (!exportTarget) {
      // Export per tutte le postazioni
      if (format === "pdf") {
        await exportToPDF();
      } else if (format === "json") {
        await exportToJsonBackup();
      } else {
        await exportToExcel();
      }
    } else {
      // Export per postazione specifica
      if (format === "pdf") {
        await exportToPDF(
          exportTarget.postazioneId,
          exportTarget.postazioneNome
        );
      } else if (format === "json") {
        await exportToJsonBackup(
          exportTarget.postazioneId,
          exportTarget.postazioneNome
        );
      } else {
        await exportToExcel(
          exportTarget.postazioneId,
          exportTarget.postazioneNome
        );
      }
    }
    closeExportModal();
  };
  // Renderizza la cella di un turno
  const renderTurnoCell = (date, slot, postazione) => {
    console.log("🎯 Rendering cella per:", {
      date,
      slotId: slot.id,
      orarioInizio: slot.orario_inizio,
      orarioFine: slot.orario_fine,
      postazione: postazione.luogo,
    });

    const availableVolunteers = getAvailableVolunteers(
      date,
      slot.orario_inizio,
      slot.orario_fine,
      slot.id
    );

    const existingAssignments = getExistingAssignments(
      date,
      slot.id,
      postazione.id
    );
    const key = `${date}-${slot.id}-${postazione.id}`;
    const pendingAssignmentsForSlot = pendingAssignments.get(key) || [];
    const normalizeConiugeId = (value) => {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? null : parsed;
    };
    const getConiugeIdForVolunteer = (volId, rawConiuge) => {
      const direct = normalizeConiugeId(rawConiuge);
      if (direct) return direct;
      const base = getVolunteerBaseData(volId);
      return normalizeConiugeId(base?.coniuge_id);
    };

    // Filtra le assegnazioni pending che sono state rimosse
    // (non dovrebbe essere necessario ma è una doppia verifica)
    const validPendingAssignments = pendingAssignmentsForSlot.filter(
      (pending) => {
        for (const [assegnazioneId, volontariSet] of pendingRemovals) {
          if (!volontariSet || volontariSet.size === 0) continue;
          const matchingExisting = existingAssignments.find(
            (a) =>
              a.assegnazione_id === parseInt(assegnazioneId, 10) &&
              a.volontario_id === pending.volontario_id
          );
          if (matchingExisting && volontariSet.has(pending.volontario_id)) {
            return false;
          }
        }
        return true;
      }
    );

    const pendingDetails = validPendingAssignments.map((pending) => {
      const volunteerData = data?.disponibilita?.find(
        (d) =>
          d.volontario_id === pending.volontario_id &&
          d.data === date &&
          d.slot_orario_id === slot.id
      );
      const baseInfo =
        volunteerData || getVolunteerBaseData(pending.volontario_id) || {};
      const displayName = baseInfo.nome
        ? `${baseInfo.nome} ${baseInfo.cognome}`
        : `Volontario ${pending.volontario_id}`;
      const conflictDates = getWeeklyConflictDates(
        pending.volontario_id,
        date
      );
      const coniugeId = getConiugeIdForVolunteer(
        pending.volontario_id,
        baseInfo.coniuge_id
      );
      const hasWeeklyConflict = conflictDates.length > 0;
      const hasSameDayConflict = hasDailyConflict(
        pending.volontario_id,
        date
      );
      const tooltipText =
        hasWeeklyConflict && conflictDates.length > 0
          ? `già in turno in data ${conflictDates
              .map(formatDateForTooltip)
              .join(", ")}`
          : hasSameDayConflict
          ? "Già assegnato in un altro slot nella stessa data"
          : displayName;
      return {
        pending,
        baseInfo,
        displayName,
        conflictDates,
        hasWeeklyConflict,
        hasSameDayConflict,
        tooltipText,
        coniugeId,
      };
    });

    const assignedIdsForHeart = new Set();
    const spouseMapForHeart = new Map();
    const registerSpouseInfo = (volId, coniugeId) => {
      const normalizedVolId = parseInt(volId, 10);
      if (Number.isNaN(normalizedVolId)) return;
      assignedIdsForHeart.add(normalizedVolId);
      if (coniugeId) {
        spouseMapForHeart.set(normalizedVolId, coniugeId);
      }
    };

    existingAssignments.forEach((assignment) => {
      registerSpouseInfo(
        assignment.volontario_id,
        getConiugeIdForVolunteer(
          assignment.volontario_id,
          assignment.coniuge_id
        )
      );
    });
    pendingDetails.forEach((detail) =>
      registerSpouseInfo(detail.pending.volontario_id, detail.coniugeId)
    );

    const renderSpouseIcon = (volId) => {
      const normalizedVolId = parseInt(volId, 10);
      if (Number.isNaN(normalizedVolId)) return null;
      let coniugeId = spouseMapForHeart.get(normalizedVolId);
      if (!coniugeId) {
        const base = getVolunteerBaseData(normalizedVolId);
        coniugeId = getConiugeIdForVolunteer(normalizedVolId, base?.coniuge_id);
        if (coniugeId) {
          spouseMapForHeart.set(normalizedVolId, coniugeId);
        }
      }
      if (!coniugeId) return null;
      const together = assignedIdsForHeart.has(coniugeId);
      const title = together
        ? "Coniuge presente nello stesso turno"
        : "Coniuge non presente in questo turno";
      return (
        <HeartIcon
          className={`h-4 w-4 ml-2 flex-shrink-0 ${
            together ? "text-red-500" : "text-gray-300"
          }`}
          title={title}
          aria-label={title}
          aria-hidden="false"
        />
      );
    };

    const maxProclamatori = postazione.max_proclamatori || 3;
    // Usa validPendingAssignments invece di pendingAssignmentsForSlot per escludere quelle rimosse
    const totalAssignments =
      existingAssignments.length + validPendingAssignments.length;
    const isAssigned = totalAssignments > 0;
    const slotSuspended = isSlotSuspendedForDate(date, slot.id);
    const isFullyAssigned = totalAssignments >= maxProclamatori;
    const hasMan = hasManInSlot(date, slot.id, postazione.id);

    // Uno slot annullato resta sempre non assegnabile
    if (slotSuspended) {
      return (
        <div
          className="text-center p-2 bg-red-50 text-red-700 text-xs border border-red-200 rounded"
        >
          Turno annullato
        </div>
      );
    }

    // Se è già completamente assegnato
    if (isFullyAssigned) {
      // Determina il colore: verde se c'è almeno un uomo, arancione se no
      const bgColor = hasMan ? "bg-green-100" : "bg-orange-100";
      const borderColor = hasMan ? "border-green-300" : "border-orange-300";
      const textColor = hasMan ? "text-green-700" : "text-orange-700";

      return (
        <div
          className={`text-center p-2 ${bgColor} border ${borderColor} rounded`}
        >
          <div className="flex items-center justify-center mb-1">
            <div className={`text-xs ${textColor} font-medium`}>
              {totalAssignments}/{maxProclamatori} assegnati
            </div>
            {!hasMan && (
              <ExclamationTriangleIcon
                className="h-4 w-4 ml-1 text-orange-600"
                title="Manca almeno un uomo nel gruppo"
              />
            )}
          </div>

          {/* Mostra assegnazioni esistenti */}
          {existingAssignments.map((assignment, index) => (
            <div
              key={`existing-${index}`}
              className={`flex items-center justify-between text-xs mb-1 ${textColor}`}
            >
              <div className="flex items-center flex-1 min-w-0">
                <TurnVolunteerName name={`${assignment.nome} ${assignment.cognome}`}
                  warning={dailyWarningFor(assignment, date)} />
                {renderSpouseIcon(assignment.volontario_id)}
                <span
                  className={`text-xs ${
                    hasMan ? "bg-green-200" : "bg-orange-200"
                  } px-2 py-1 rounded-full ml-2 flex-shrink-0 ${
                    hasMan ? "text-green-800" : "text-orange-800"
                  }`}
                >
                  {(() => {
                    const contatori = getContatoriDinamici(
                      assignment.volontario_id
                    );
                    return `${contatori.assegnazioni_totali} - ${contatori.disponibilita_totali}`;
                  })()}
                </span>
              </div>
              <button
                onClick={() =>
                  handleRemoveVolunteer(
                    assignment.assegnazione_id,
                    assignment.volontario_id
                  )
                }
                className="text-red-600 hover:text-red-800 ml-1"
                title="Rimuovi volontario"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            </div>
          ))}

          {/* Mostra assegnazioni in sospeso */}
          {pendingDetails.map((detail, index) => {
            const {
              pending,
              displayName,
              hasWeeklyConflict,
              tooltipText,
            } = detail;
            return (
              <div
                key={`pending-${index}`}
                className="flex items-center justify-between text-xs mb-1 text-orange-700 bg-orange-50 px-1 rounded"
              >
                <div className="flex items-center flex-1 min-w-0">
                  <TurnVolunteerName name={displayName} tooltip={tooltipText}
                    className={hasWeeklyConflict ? "bg-yellow-200 px-1" : ""}
                    warning={dailyWarningFor(pending, date)} />
                <span className="text-xs ml-2 bg-orange-200 px-1 py-0.5 rounded flex-shrink-0">
                  (in sospeso)
                </span>
                {renderSpouseIcon(pending.volontario_id)}
                {detail.baseInfo?.nome && (
                  <span className="text-xs bg-orange-300 px-2 py-1 rounded-full ml-2 flex-shrink-0">
                    {(() => {
                      const contatori = getContatoriDinamici(
                        pending.volontario_id
                      );
                        return `${contatori.assegnazioni_totali} - ${contatori.disponibilita_totali}`;
                      })()}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    const currentPending = pendingAssignments.get(key) || [];
                    const updatedPending = currentPending.filter(
                      (p) => p.volontario_id !== pending.volontario_id
                    );

                    const newPendingAssignments = new Map(pendingAssignments);
                    if (updatedPending.length > 0) {
                      newPendingAssignments.set(key, updatedPending);
                    } else {
                      newPendingAssignments.delete(key);
                    }
                    setPendingAssignments(newPendingAssignments);
                    toastSuccess(
                      "Volontario rimosso dalle assegnazioni in sospeso"
                    );
                  }}
                  className="text-red-600 hover:text-red-800 ml-1"
                  title="Rimuovi dalle assegnazioni in sospeso"
                >
                  <TrashIcon className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      );
    }

    // Se è parzialmente assegnato
    if (isAssigned) {
      // Per slot parziali: giallo se ha uomo o nessuno ancora, arancione-giallo se senza uomo
      const bgColor = hasMan ? "bg-yellow-100" : "bg-orange-50";
      const borderColor = hasMan ? "border-yellow-300" : "border-orange-300";
      const textColor = hasMan ? "text-yellow-700" : "text-orange-700";

      return (
        <div
          className={`text-center p-2 ${bgColor} border ${borderColor} rounded`}
        >
          <div className="flex items-center justify-center mb-1">
            <div className={`text-xs ${textColor} font-medium`}>
              {totalAssignments}/{maxProclamatori} assegnati
            </div>
            {!hasMan && (
              <ExclamationTriangleIcon
                className="h-4 w-4 ml-1 text-orange-600"
                title="Manca almeno un uomo nel gruppo"
              />
            )}
          </div>

          {/* Mostra assegnazioni esistenti */}
          {existingAssignments.map((assignment, index) => (
            <div
              key={`existing-${index}`}
              className={`flex items-center justify-between text-xs mb-1 ${textColor}`}
            >
              <div className="flex items-center flex-1 min-w-0">
                <TurnVolunteerName name={`${assignment.nome} ${assignment.cognome}`}
                  warning={dailyWarningFor(assignment, date)} />
                {renderSpouseIcon(assignment.volontario_id)}
                <span
                  className={`text-xs ${
                    hasMan ? "bg-yellow-200" : "bg-orange-200"
                  } px-2 py-1 rounded-full ml-2 flex-shrink-0 ${
                    hasMan ? "text-yellow-800" : "text-orange-800"
                  }`}
                >
                  {(() => {
                    const contatori = getContatoriDinamici(
                      assignment.volontario_id
                    );
                    return `${contatori.assegnazioni_totali} - ${contatori.disponibilita_totali}`;
                  })()}
                </span>
              </div>
              <button
                onClick={() =>
                  handleRemoveVolunteer(
                    assignment.assegnazione_id,
                    assignment.volontario_id
                  )
                }
                className="text-red-600 hover:text-red-800 ml-1"
                title="Rimuovi volontario"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            </div>
          ))}

          {/* Mostra assegnazioni in sospeso */}
          {pendingDetails.map((detail, index) => {
            const {
              pending,
              displayName,
              hasWeeklyConflict,
              tooltipText,
            } = detail;
            return (
              <div
                key={`pending-${index}`}
                className="flex items-center justify-between text-xs mb-1 text-orange-700 bg-orange-50 px-1 rounded"
              >
                <div className="flex items-center flex-1 min-w-0">
                  <TurnVolunteerName name={displayName} tooltip={tooltipText}
                    className={hasWeeklyConflict ? "bg-yellow-200 px-1" : ""}
                    warning={dailyWarningFor(pending, date)} />
                  <span className="text-xs ml-2 bg-orange-200 px-1 py-0.5 rounded flex-shrink-0">
                    (in sospeso)
                  </span>
                  {renderSpouseIcon(pending.volontario_id)}
                  {detail.baseInfo?.nome && (
                    <span className="text-xs bg-orange-300 px-2 py-1 rounded-full ml-2 flex-shrink-0">
                      {(() => {
                        const contatori = getContatoriDinamici(
                          pending.volontario_id
                        );
                        return `${contatori.assegnazioni_totali} - ${contatori.disponibilita_totali}`;
                      })()}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    const currentPending = pendingAssignments.get(key) || [];
                    const updatedPending = currentPending.filter(
                      (p) => p.volontario_id !== pending.volontario_id
                    );

                    const newPendingAssignments = new Map(pendingAssignments);
                    if (updatedPending.length > 0) {
                      newPendingAssignments.set(key, updatedPending);
                    } else {
                      newPendingAssignments.delete(key);
                    }
                    setPendingAssignments(newPendingAssignments);
                    toastSuccess(
                      "Volontario rimosso dalle assegnazioni in sospeso"
                    );
                  }}
                  className="text-red-600 hover:text-red-800 ml-1"
                  title="Rimuovi dalle assegnazioni in sospeso"
                >
                  <TrashIcon className="h-3 w-3" />
                </button>
              </div>
            );
          })}
          {(() => {
            const occupiedIds = new Set([
              ...existingAssignments.map((assignment) =>
                parseInt(assignment.volontario_id, 10)
              ),
              ...validPendingAssignments.map((assignment) =>
                parseInt(assignment.volontario_id, 10)
              ),
            ]);
            const availableIdsSet = new Set(
              availableVolunteers.map((vol) =>
                parseInt(vol.volontario_id, 10)
              )
            );
            const selectableVolunteers = sortManualSelectionList(
              [...availableVolunteers].filter(
                (v) =>
                  !existingAssignments.some(
                    (a) => a.volontario_id === v.volontario_id
                  ) &&
                  !validPendingAssignments.some(
                    (p) => p.volontario_id === v.volontario_id
                  )
              )
            );
            const extraVolunteers = sortManualSelectionList(
              buildNonAvailableVolunteers(availableIdsSet, occupiedIds)
            );

            return (
              <VolunteerPicker
                className="mt-1"
                availableVolunteers={selectableVolunteers}
                extraVolunteers={extraVolunteers}
                getCounters={getContatoriDinamici}
                context={`${postazione.luogo} · ${formatDateForTooltip(date)} · ${slot.orario_inizio.slice(0, 5)} - ${slot.orario_fine.slice(0, 5)}`}
                onSelect={(id) => handleManualAssignment(date, slot.id, postazione.id, id)}
              />
            );
          })()}
        </div>
      );
    }

    // Se non è assegnato (anche senza disponibilità registrate)
    return (
      <div className="text-center p-2 bg-blue-100 border border-blue-300 rounded">
        <div className="text-xs text-blue-700 font-medium mb-1">
          0/{maxProclamatori} assegnati
        </div>
        {(() => {
          const occupiedIds = new Set([
            ...existingAssignments.map((assignment) =>
              parseInt(assignment.volontario_id, 10)
            ),
            ...validPendingAssignments.map((assignment) =>
              parseInt(assignment.volontario_id, 10)
            ),
          ]);
          const availableIdsSet = new Set(
            availableVolunteers.map((vol) =>
              parseInt(vol.volontario_id, 10)
            )
          );
          const selectableVolunteers = sortManualSelectionList(
            [...availableVolunteers].filter(
              (v) =>
                !existingAssignments.some(
                  (a) => a.volontario_id === v.volontario_id
                ) &&
                !validPendingAssignments.some(
                  (p) => p.volontario_id === v.volontario_id
                )
            )
          );
          const extraVolunteers = sortManualSelectionList(
            buildNonAvailableVolunteers(availableIdsSet, occupiedIds)
          );
          return (
            <VolunteerPicker
              placeholder="Seleziona proclamatore..."
              data-slot={slot.id}
              data-postazione={postazione.id}
              data-date={date}
              availableVolunteers={selectableVolunteers}
              extraVolunteers={extraVolunteers}
              getCounters={getContatoriDinamici}
              context={`${postazione.luogo} · ${formatDateForTooltip(date)} · ${slot.orario_inizio.slice(0, 5)} - ${slot.orario_fine.slice(0, 5)}`}
              onSelect={(id) => handleManualAssignment(date, slot.id, postazione.id, id)}
            />
          );
        })()}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">

      {saving && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg px-6 py-4 shadow-lg flex items-center space-x-3">
            <span className="h-5 w-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium text-gray-700">
              Salvataggio delle modifiche in corso...
            </span>
          </div>
        </div>
      )}

      {resettingInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg px-6 py-5 shadow-xl flex items-center space-x-4">
            <span className="h-6 w-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {resettingInfo.label}
              </p>
              <p className="text-xs text-gray-600">
                {resettingInfo.description}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Gestione Turni - Vista Mensile
        </h1>
        <p className="text-gray-600">
          Gestisci le assegnazioni dei turni per tutte le postazioni. Naviga tra
          i mesi e usa l'autocompilazione per assegnare automaticamente i
          volontari disponibili per il mese selezionato.
        </p>
      </div>
      {/* Informazioni */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg">
        <button
          onClick={() => setShowInstructions((prev) => !prev)}
          className="w-full flex items-center justify-between px-4 py-3 text-blue-900 text-sm font-medium"
        >
          <span>📘 Istruzioni e legenda</span>
          <svg
            className={`h-5 w-5 text-blue-600 transform transition-transform ${
              showInstructions ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {showInstructions && (
          <div className="px-4 pb-4 text-sm text-blue-700 space-y-2">
            <p>
              • Usa il pulsante "Autocompila tutte le postazioni" per calcolare l'intero mese della congregazione in modo piu accurato e bilanciato
            </p>
            <p>
              • Usa il pulsante "Autocompilazione" dentro una singola postazione solo per interventi mirati: lavora su quella postazione e vede meno contesto globale
            </p>
            <p>
              • Usa il pulsante "Reset" per azzerare completamente il mese aperto: vengono eliminate le assegnazioni di tutte le postazioni e anche la bozza locale non salvata
            </p>
            <p>
              • Naviga tra i mesi usando le frecce o il pulsante "Vai al mese corrente"
            </p>
            <p>• Puoi modificare manualmente le assegnazioni dopo l'autocompilazione</p>
            <p>• Clicca "Salva Modifiche" per confermare le assegnazioni</p>
            <p className="font-medium mt-2 mb-1">📊 Legenda numeri tra parentesi accanto ai nomi:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>
                <strong>Primo numero</strong>: Assegnazioni totali del volontario nel mese selezionato
              </li>
              <li>
                <strong>Secondo numero</strong>: Disponibilità totali del volontario nel mese selezionato
              </li>
              <li>
                <strong>Formato</strong>: (assegnazioni totali - disponibilità totali)
              </li>
              <li className="text-blue-600">
                <strong>Esempio</strong>: Mario Rossi (8 - 32) = 8 assegnazioni totali, 32 disponibilità totali
              </li>
            </ul>
            <p className="font-medium mt-2 mb-1">🎨 Legenda colori degli slot:</p>
            <ul className="ml-4 space-y-1 text-blue-700">
              <li>
                <span className="inline-block w-3 h-3 bg-blue-100 border border-blue-300 rounded mr-1"></span>
                <strong>Blu</strong>: Slot vuoto (nessun volontario assegnato)
              </li>
              <li>
                <span className="inline-block w-3 h-3 bg-yellow-100 border border-yellow-300 rounded mr-1"></span>
                <strong>Giallo</strong>: Assegnazione parziale (non tutti i posti coperti)
              </li>
              <li>
                <span className="inline-block w-3 h-3 bg-green-100 border border-green-300 rounded mr-1"></span>
                <strong>Verde</strong>: Assegnazione completa con almeno un uomo
              </li>
              <li>
                <span className="inline-block w-3 h-3 bg-orange-100 border border-orange-300 rounded mr-1"></span>
                <strong>Arancione</strong>: Assegnazione completa ma manca almeno un uomo <span className="text-orange-600">⚠️</span>
              </li>
            </ul>
          </div>
        )}
      </div>
      {/* Filtri */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
            <h3 className="text-2xl font-semibold text-gray-900 capitalize">
              {getMonthName(selectedMonth.year, selectedMonth.month)}
            </h3>
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <button
                onClick={goToPreviousMonth}
                className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 text-gray-600"
                title="Vai al mese precedente"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <button
                onClick={goToCurrentMonth}
                className={`btn-primary flex items-center ${
                  isCurrentMonth() ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title={isCurrentMonth() ? "Mese corrente" : "Vai al mese corrente"}
                disabled={isCurrentMonth()}
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {isCurrentMonth() ? "Mese corrente" : "Vai al mese corrente"}
              </button>
              <button
                onClick={goToNextMonth}
                className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 text-gray-600"
                title="Vai al mese successivo"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button
              onClick={() => executeAutocompilazione()}
              disabled={compiling || saving}
              className="btn-primary flex items-center justify-center whitespace-nowrap"
              title={`Esegui l'autocompilazione globale di tutte le postazioni nel mese ${getMonthName(
                selectedMonth.year,
                selectedMonth.month
              )}`}
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              {compiling ? "Autocompilazione..." : "Autocompila tutte le postazioni"}
            </button>
            <button
              onClick={() => openExportModal()}
              disabled={exporting || saving}
              className="btn-primary flex items-center justify-center whitespace-nowrap"
              title={`Esporta tabella per tutte le postazioni nel mese ${getMonthName(
                selectedMonth.year,
                selectedMonth.month
              )}`}
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              {exporting ? "Esportando..." : "Esporta tutte"}
            </button>
            <button
              onClick={handleReset}
              disabled={compiling || saving}
              className="btn-danger flex items-center justify-center whitespace-nowrap"
              title={`Elimina tutte le assegnazioni del mese ${getMonthName(
                selectedMonth.year,
                selectedMonth.month
              )} e pulisce anche la bozza locale`}
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Reset
            </button>
            <button
              onClick={handleClearCache}
              disabled={clearingCache || compiling || saving}
              className="btn-secondary flex items-center justify-center whitespace-nowrap"
              title="Pulisci i dati locali e ricarica i turni aggiornati"
            >
              <svg
                className="h-4 w-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582M20 20v-5h-.581M5 9a7 7 0 0112.585-3M19 15a7 7 0 01-12.585 3"
                />
              </svg>
              {clearingCache ? "Cancellazione..." : "Cancella cache"}
            </button>
          </div>
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              📅 {formatDate(selectedDateRange.inizio)} - {formatDate(selectedDateRange.fine)}
            </p>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
              L&apos;azione consigliata per il mese e&apos; <strong>Autocompila tutte le postazioni</strong>.
              L&apos;autocompilazione della singola postazione resta disponibile per correzioni mirate e puo&apos; produrre un risultato meno accurato sul totale del mese.
            </div>
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sky-800">
              🔄 <strong>Autocompila tutte le postazioni</strong> calcola il mese considerando tutte
              le postazioni. <strong>Reset</strong> ripulisce sempre l&apos;intero mese aperto e
              azzera anche le modifiche locali in sospeso.
            </div>
          </div>
        </div>
      </div>
      {/* Contenuto Gestione Turni */}
      <div className="space-y-6">
        {data?.postazioni?.map((postazione) => {
          // Crea un dateRange specifico per questa postazione: solo giorni validi
          const postazioneDateRange = (data?.dateRange || []).filter((date) =>
            isPostazioneActiveForDate(postazione, date)
          );

          return (
            <div key={postazione.id} className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  <MapPinIcon className="h-5 w-5 inline mr-2 text-primary-600" />
                  {postazione.luogo}
                </h3>
                      {isPostazioneComplete(postazione) ? (
                        <CheckCircleIcon
                          className="h-6 w-6 text-green-500"
                          title="Postazione completa per questo mese"
                        />
                      ) : (
                        <ClockIcon
                          className="h-6 w-6 text-orange-500"
                          title="Postazione non ancora completa per questo mese"
                        />
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {postazione.indirizzo}
                    </p>
                    <p className="text-sm text-primary-600 font-medium mt-1">
                      Max proclamatori per turno:{" "}
                      {postazione.max_proclamatori || 3}
                    </p>
                  </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end w-full lg:w-auto">
                    {/* Pulsante Autocompilazione */}
                    <button
                      onClick={() =>
                        executeAutocompilazione(postazione.id, postazione.luogo)
                      }
                      disabled={compiling || saving}
                      className="btn-secondary flex items-center justify-center whitespace-nowrap w-full sm:w-auto"
                      title={`Esegui autocompilazione automatica per "${
                        postazione.luogo
                      }" nel mese ${getMonthName(
                        selectedMonth.year,
                        selectedMonth.month
                      )}`}
                    >
                      <PlayIcon className="h-4 w-4 mr-2" />
                      {compiling ? "Autocompilazione..." : "Autocompilazione"}
                    </button>

                    {/* Pulsante Export Unificato */}
                    <button
                      onClick={() =>
                        openExportModal(postazione.id, postazione.luogo)
                      }
                      disabled={exporting || saving}
                      className="btn-secondary flex items-center justify-center whitespace-nowrap w-full sm:w-auto"
                      title={`Esporta tabella per "${
                        postazione.luogo
                      }" nel mese ${getMonthName(
                        selectedMonth.year,
                        selectedMonth.month
                      )}`}
                    >
                      <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                      {exporting ? "Esportando..." : "Export"}
                    </button>

                    {/* Pulsante Salva modifiche */}
                    {hasPendingChanges && (
                      <button
                        onClick={savePendingChanges}
                        disabled={saving || compiling}
                        className={`inline-flex items-center justify-center px-4 py-2 rounded-md text-white w-full sm:w-auto ${
                          saving
                            ? "bg-green-500 opacity-70"
                            : "bg-green-600 hover:bg-green-700 animate-pulse"
                        }`}
                        title="Salva tutte le modifiche in sospeso"
                      >
                        {saving ? (
                          <>
                            <span className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Salvataggio...
                          </>
                        ) : (
                          <>
                            <svg
                              className="h-4 w-4 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Salva Modifiche
                          </>
                        )}
                      </button>
                    )}

                    <button
                      onClick={handleSendNotifications}
                      disabled={
                        notifyLoading ||
                        saving ||
                        compiling ||
                        hasPendingChanges
                      }
                      className="inline-flex items-center justify-center px-4 py-2 rounded-md text-white w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
                      title="Invia notifiche push degli incarichi salvati"
                    >
                      {notifyLoading ? (
                        <>
                          <span className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Invio notifiche...
                        </>
                      ) : (
                        <>
                          <BellIcon className="h-4 w-4 mr-2" />
                          Invia notifiche
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className={isMobile ? "space-y-4" : "overflow-x-auto"}>
                {isMobile ? (
                  <div className="space-y-4">
                    {postazione.slot_orari?.map((slot) => (
                      <div
                        key={slot.id}
                        className="bg-gray-50 rounded-xl border border-gray-200 p-3 shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <ClockIcon className="h-5 w-5 text-primary-600" />
                            <div className="text-sm font-semibold text-gray-800">
                              {formatTime(slot.orario_inizio)} -{" "}
                              {formatTime(slot.orario_fine)}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            {postazione.max_proclamatori || 3} posti
                          </div>
                        </div>
                        <div className="space-y-3">
                          {postazioneDateRange.map((date) => (
                            <div
                              key={`${slot.id}-${date}`}
                              className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm"
                            >
                              <div className="flex items-center justify-between text-sm font-medium text-gray-700 mb-2">
                                <span>{getDayName(date)}</span>
                                <span className="text-xs text-gray-500">
                                  {formatDate(date)}
                                </span>
                              </div>
                              {renderTurnoCell(date, slot, postazione)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          style={{
                            borderRight: "1px solid #d1d5db",
                            boxShadow: "1px 0 0 0 #d1d5db",
                          }}
                        >
                          Orario
                        </th>
                        {postazioneDateRange.map((date) => {
                          return (
                            <th
                              key={date}
                              className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
                            >
                              <div>{getDayName(date)}</div>
                              <div className="text-xs text-gray-400">
                                {formatDate(date)}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {postazione.slot_orari?.map((slot) => (
                        <tr key={slot.id}>
                          <td
                            className="sticky left-0 z-10 bg-white px-2 py-2 text-center"
                            style={{
                              borderRight: "1px solid #d1d5db",
                              boxShadow: "1px 0 0 0 #d1d5db",
                            }}
                          >
                            <div className="flex flex-col items-center justify-center space-y-0.5">
                              <ClockIcon className="h-4 w-4 text-primary-600" />
                              <span className="text-xs font-medium text-gray-900 leading-tight">
                                {formatTime(slot.orario_inizio)}
                              </span>
                              <span className="text-xs font-medium text-gray-900 leading-tight">
                                {formatTime(slot.orario_fine)}
                              </span>
                            </div>
                          </td>
                          {postazioneDateRange.map((date) => {
                            return (
                              <td
                                key={`${slot.id}-${date}`}
                                className="px-3 py-2"
                              >
                                {renderTurnoCell(date, slot, postazione)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {reportSummary && (
        <div className="mt-6 bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Report</h3>
              <p className="text-sm text-gray-600">
                Riepilogo del mese basato sui dati salvati.
              </p>
            </div>
            <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
              {reportSummary.assignedSuccessfullyCount} proclamatori assegnati con successo
            </div>
          </div>

          {hasPendingChanges && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Il report mostra l&apos;ultimo salvataggio completato. Salva le modifiche in sospeso
              per aggiornarlo.
            </div>
          )}

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
              <button
                type="button"
                onClick={() =>
                  setIsZeroAvailabilityReportCollapsed(
                    !isZeroAvailabilityReportCollapsed
                  )
                }
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                <div className="flex items-center gap-3">
                  <ChevronRightIcon
                    className={`h-4 w-4 text-rose-700 transition-transform ${
                      isZeroAvailabilityReportCollapsed ? "" : "rotate-90"
                    }`}
                  />
                  <h4 className="text-sm font-semibold text-rose-900">
                    Non assegnati per 0 disponibilità
                  </h4>
                </div>
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-rose-700">
                  {reportSummary.zeroAvailabilityUnassigned.length}
                </span>
              </button>
              {!isZeroAvailabilityReportCollapsed &&
                (reportSummary.zeroAvailabilityUnassigned.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {reportSummary.zeroAvailabilityUnassigned.map((volunteer) => (
                      <span
                        key={volunteer.id}
                        className="rounded-full bg-white px-3 py-1 text-sm text-rose-800 border border-rose-200"
                      >
                        {formatVolunteerFullName(volunteer)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-rose-800">
                    Nessun proclamatore in questa categoria.
                  </p>
                ))}
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <button
                type="button"
                onClick={() =>
                  setIsAvailableUnassignedReportCollapsed(
                    !isAvailableUnassignedReportCollapsed
                  )
                }
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                <div className="flex items-center gap-3">
                  <ChevronRightIcon
                    className={`h-4 w-4 text-amber-700 transition-transform ${
                      isAvailableUnassignedReportCollapsed ? "" : "rotate-90"
                    }`}
                  />
                  <h4 className="text-sm font-semibold text-amber-900">
                    Non assegnati con disponibilità inserite
                  </h4>
                </div>
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-amber-700">
                  {reportSummary.availableButUnassigned.length}
                </span>
              </button>
              {!isAvailableUnassignedReportCollapsed &&
                (reportSummary.availableButUnassigned.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {reportSummary.availableButUnassigned.map((volunteer) => (
                      <span
                        key={volunteer.id}
                        className="rounded-full bg-white px-3 py-1 text-sm text-amber-900 border border-amber-200"
                      >
                        {formatVolunteerFullName(volunteer)} ({volunteer.disponibilitaTotali} disponibilità)
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-amber-900">
                    Nessun proclamatore con disponibilità rimasto senza assegnazione.
                  </p>
                ))}
            </div>

            <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
              <button
                type="button"
                onClick={() =>
                  setIsUnavailableForMonthReportCollapsed(
                    !isUnavailableForMonthReportCollapsed
                  )
                }
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                <div className="flex items-center gap-3">
                  <ChevronRightIcon
                    className={`h-4 w-4 text-sky-700 transition-transform ${
                      isUnavailableForMonthReportCollapsed ? "" : "rotate-90"
                    }`}
                  />
                  <h4 className="text-sm font-semibold text-sky-900">
                    Non disponibili nel mese corrente
                  </h4>
                </div>
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-sky-700">
                  {reportSummary.unavailableForMonth.length}
                </span>
              </button>
              {!isUnavailableForMonthReportCollapsed &&
                (reportSummary.unavailableForMonth.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {reportSummary.unavailableForMonth.map((volunteer) => (
                      <span
                        key={volunteer.id}
                        className="rounded-full bg-white px-3 py-1 text-sm text-sky-900 border border-sky-200"
                      >
                        {formatVolunteerFullName(volunteer)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-sky-900">
                    Nessun proclamatore ha dichiarato l&apos;indisponibilità per
                    questo mese.
                  </p>
                ))}
            </div>
          </div>

          <p className="mt-4 text-xs text-gray-500">
            Totale proclamatori considerati nel mese: {reportSummary.totalVolunteers}
          </p>
        </div>
      )}

      {/* Modal di selezione formato export */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Seleziona formato di export
              </h3>
              <button
                onClick={closeExportModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                {exportTarget
                  ? `Esporta tabella per "${
                      exportTarget.postazioneNome
                    }" nel mese ${getMonthName(
                      selectedMonth.year,
                      selectedMonth.month
                    )}`
                  : `Esporta tabella per tutte le postazioni nel mese ${getMonthName(
                      selectedMonth.year,
                      selectedMonth.month
                    )}`}
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleExport("pdf")}
                disabled={exporting || saving}
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                <svg
                  className="h-5 w-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0 1 1 0 102 0zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {exporting ? "Esportando PDF..." : "Esporta come PDF"}
              </button>

              <button
                onClick={() => handleExport("excel")}
                disabled={exporting || saving}
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <svg
                  className="h-5 w-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
                    clipRule="evenodd"
                  />
                </svg>
                {exporting ? "Esportando Excel..." : "Esporta come Excel (CSV)"}
              </button>

              <button
                onClick={() => handleExport("json")}
                disabled={exporting || saving}
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
              >
                <svg
                  className="h-5 w-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h6a1 1 0 100-2H5V5h10v3a1 1 0 102 0V5a2 2 0 00-2-2H5z" />
                  <path d="M12.293 11.293a1 1 0 011.414 0L16 13.586l2.293-2.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" />
                </svg>
                {exporting
                  ? "Esportando JSON..."
                  : "Esporta snapshot JSON"}
              </button>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-200 space-y-3">
              <div className="rounded-md border border-sky-200 bg-sky-50 p-3">
                <p className="text-sm font-medium text-sky-900">
                  Verifica snapshot JSON
                </p>
                <p className="mt-1 text-xs text-sky-800">
                  Carica un file snapshot per controllare coerenza, riferimenti
                  e compatibilità prima del futuro restore.
                </p>
                <button
                  onClick={() => {
                    closeExportModal();
                    triggerSnapshotValidationPicker();
                  }}
                  disabled={validatingSnapshot}
                  className="mt-3 w-full px-4 py-2 text-sm font-medium text-sky-700 bg-white border border-sky-300 rounded-md hover:bg-sky-100 disabled:opacity-50"
                >
                  {validatingSnapshot
                    ? "Validazione in corso..."
                    : "Seleziona snapshot da validare"}
                </button>
              </div>

              <button
                onClick={closeExportModal}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      <input
        ref={snapshotFileInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={handleSnapshotFileSelected}
      />

      {showSnapshotValidationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg max-w-3xl w-full mx-4 shadow-xl max-h-[85vh] overflow-hidden">
            {restoringSnapshot && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/90 backdrop-blur-[1px]">
                <div className="mx-6 max-w-md rounded-xl border border-sky-100 bg-white px-6 py-5 text-center shadow-lg">
                  <div className="h-12 w-12 mx-auto border-4 border-sky-200 border-t-sky-600 rounded-full animate-spin" />
                  <h3 className="mt-4 text-base font-semibold text-gray-900">
                    Ripristino snapshot in corso
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Sono in fase di elaborazione molti dati, per favore attendi qualche minuto...
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Validazione snapshot JSON
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {snapshotValidationFilename
                    ? snapshotValidationFilename
                    : "Seleziona un file snapshot per avviare la verifica"}
                </p>
              </div>
              <button
                onClick={closeSnapshotValidationModal}
                disabled={validatingSnapshot || restoringSnapshot}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 overflow-y-auto max-h-[calc(85vh-148px)]">
              {validatingSnapshot ? (
                <div className="py-10 text-center">
                  <div className="h-10 w-10 mx-auto border-4 border-sky-200 border-t-sky-600 rounded-full animate-spin" />
                  <p className="mt-4 text-sm text-gray-700">
                    Verifica dello snapshot in corso...
                  </p>
                </div>
              ) : snapshotValidationResult ? (
                <div className="space-y-6">
                  {snapshotRestoreResult?.restored && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <p className="text-sm font-semibold text-green-900">
                        Restore completato
                      </p>
                      <div className="mt-2 text-sm text-green-800 space-y-1">
                        <p>
                          Assegnazioni ripristinate:{" "}
                          {
                            snapshotRestoreResult.restore_summary
                              ?.inserted_assegnazioni
                          }
                        </p>
                        <p>
                          Volontari turno ripristinati:{" "}
                          {
                            snapshotRestoreResult.restore_summary
                              ?.inserted_assegnazioni_volontari
                          }
                        </p>
                        <p>
                          Disponibilita ripristinate:{" "}
                          {
                            snapshotRestoreResult.restore_summary
                              ?.inserted_disponibilita
                          }
                        </p>
                        <p>
                          Slot sospesi ripristinati:{" "}
                          {
                            snapshotRestoreResult.restore_summary
                              ?.inserted_suspended_slots
                          }
                        </p>
                      </div>
                    </div>
                  )}

                  <div
                    className={`rounded-lg border p-4 ${
                      snapshotValidationResult.valid
                        ? "border-green-200 bg-green-50"
                        : "border-amber-200 bg-amber-50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {snapshotValidationResult.valid ? (
                        <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p
                          className={`text-sm font-semibold ${
                            snapshotValidationResult.valid
                              ? "text-green-900"
                              : "text-amber-900"
                          }`}
                        >
                          {snapshotValidationResult.valid
                            ? "Snapshot valido"
                            : "Snapshot non pronto per un restore sicuro"}
                        </p>
                        <p
                          className={`mt-1 text-sm ${
                            snapshotValidationResult.valid
                              ? "text-green-800"
                              : "text-amber-800"
                          }`}
                        >
                          Errori bloccanti:{" "}
                          {snapshotValidationResult.blocking_errors?.length || 0}
                          {" · "}
                          Warning:{" "}
                          {snapshotValidationResult.warnings?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {snapshotValidationResult.summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          Disponibilita
                        </p>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          {snapshotValidationResult.summary.disponibilita ?? 0}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          Slot sospesi
                        </p>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          {snapshotValidationResult.summary.suspended_slots ?? 0}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          Assegnazioni
                        </p>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          {snapshotValidationResult.summary.assegnazioni ?? 0}
                        </p>
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          Volontari turno
                        </p>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          {snapshotValidationResult.summary
                            .assegnazioni_volontari ?? 0}
                        </p>
                      </div>
                    </div>
                  )}

                  {snapshotValidationResult.resolved_scope && (
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <p className="text-sm font-semibold text-gray-900">
                        Scope risolto
                      </p>
                      <div className="mt-2 text-sm text-gray-700 space-y-1">
                        <p>
                          Congregazione ID:{" "}
                          {
                            snapshotValidationResult.resolved_scope
                              .congregazione_id
                          }
                        </p>
                        <p>
                          Range:{" "}
                          {snapshotValidationResult.resolved_scope.data_inizio} -{" "}
                          {snapshotValidationResult.resolved_scope.data_fine}
                        </p>
                        <p>
                          Postazione ID:{" "}
                          {snapshotValidationResult.resolved_scope
                            .postazione_id ?? "tutte"}
                        </p>
                      </div>
                    </div>
                  )}

                  {!snapshotScopeMatchesCurrentRange && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <p className="text-sm font-semibold text-amber-900">
                        Mese pagina diverso dallo snapshot
                      </p>
                      <p className="mt-1 text-sm text-amber-800">
                        Per ridurre il rischio di errore, il restore e abilitato
                        solo quando il mese aperto in pagina coincide con il
                        range dello snapshot validato.
                      </p>
                    </div>
                  )}

                  {hasPendingTurnChanges && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <p className="text-sm font-semibold text-amber-900">
                        Modifiche locali in sospeso
                      </p>
                      <p className="mt-1 text-sm text-amber-800">
                        Salva o annulla prima le modifiche turni non persistite
                        prima di eseguire il restore.
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-red-700">
                        Errori bloccanti
                      </p>
                      {snapshotValidationResult.blocking_errors?.length ? (
                        <div className="mt-2 space-y-3">
                          {snapshotValidationResult.blocking_errors.map(
                            (issue, index) => (
                              <div
                                key={`blocking-${index}`}
                                className="rounded-lg border border-red-200 bg-red-50 p-3"
                              >
                                <p className="text-sm font-medium text-red-900">
                                  {issue.message}
                                </p>
                                <p className="mt-1 text-xs font-mono text-red-700">
                                  {issue.code}
                                </p>
                                {issue.details && (
                                  <pre className="mt-2 text-xs text-red-800 bg-white border border-red-100 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                                    {formatSnapshotIssueDetails(issue.details)}
                                  </pre>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-gray-600">
                          Nessun errore bloccante rilevato.
                        </p>
                      )}
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-amber-700">
                        Warning
                      </p>
                      {snapshotValidationResult.warnings?.length ? (
                        <div className="mt-2 space-y-3">
                          {snapshotValidationResult.warnings.map(
                            (issue, index) => (
                              <div
                                key={`warning-${index}`}
                                className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                              >
                                <p className="text-sm font-medium text-amber-900">
                                  {issue.message}
                                </p>
                                <p className="mt-1 text-xs font-mono text-amber-700">
                                  {issue.code}
                                </p>
                                {issue.details && (
                                  <pre className="mt-2 text-xs text-amber-800 bg-white border border-amber-100 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                                    {formatSnapshotIssueDetails(issue.details)}
                                  </pre>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-gray-600">
                          Nessun warning rilevato.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-10 text-center">
                  <p className="text-sm text-gray-600">
                    Seleziona un file snapshot JSON per avviare la validazione.
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={handleRestoreSnapshot}
                disabled={!canRestoreValidatedSnapshot}
                className={`px-4 py-2 text-sm font-medium rounded-md text-white ${
                  canRestoreValidatedSnapshot
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-red-300 cursor-not-allowed"
                }`}
              >
                {restoringSnapshot ? "Ripristino..." : "Ripristina snapshot"}
              </button>
              <button
                onClick={triggerSnapshotValidationPicker}
                disabled={validatingSnapshot || restoringSnapshot}
                className="px-4 py-2 text-sm font-medium text-sky-700 bg-white border border-sky-300 rounded-md hover:bg-sky-50 disabled:opacity-50"
              >
                {validatingSnapshot ? "Validazione..." : "Valida un altro file"}
              </button>
              <button
                onClick={closeSnapshotValidationModal}
                disabled={validatingSnapshot || restoringSnapshot}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Autocompilazione;
